import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import CloseIcon from "@mui/icons-material/Close";
import ExtensionIcon from "@mui/icons-material/Extension";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import MusicOffIcon from "@mui/icons-material/MusicOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SettingsIcon from "@mui/icons-material/Settings";
import TollIcon from "@mui/icons-material/Toll";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    MenuItem,
    Popover,
    Tooltip,
    Typography,
    keyframes,
    useMediaQuery,
} from "@mui/material";
import { FaShoppingBasket } from "react-icons/fa";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { useConfetti } from "../../../context/ConfettiContext";
import ApiService from "../../../services/ApiService";
import AuthService from "../../../services/AuthService";
import AudioService, { AUDIO_EFFECT } from "../../../services/AudioService";
import UserService from "../../../services/UserService";
import UtilService from "../../../services/UtilService";
import HomeCreditService from "../../../services/HomeCreditService";
import { resolveWebHintPuzzleCoins } from "../../../services/PuzzleCoinsUtil";
import JigsawPlayService from "./JigsawPlayService";
import Logger from "../../../services/Logger";
import PuzzleAccessGate from "../../login/PuzzleAccessGate";
import SubscriptionDialog from "../../subscription/SubscriptionDialog";
import { trackEvent } from "../../../services/GoogleAnalytics";
import {
    buildPuzzleAccessContext,
    getJigsawTileLockReason,
    isJigsawPremiumTier,
} from "../common/puzzleTierAccess";
import { resolveJigsawShareTemplate, resolveJigsawTemplateTileSize } from "./jigsawBrowseUtils";
import { isJigsawGroupUnavailable, normalizeHomeJigsawGroup } from "./JigsawService";
import AudioButton from "../../components/AudioButton";
import { PUZZLE_BGM_TRACKS } from "../../components/bgmTracks";
import PlayToolbarScrollRow from "../../components/PlayToolbarScrollRow";
import PlayElapsedTimer, {
    clearPlayElapsedTimerPersist,
} from "../common/components/PlayElapsedTimer";
// import GamePausedOverlay from "../common/components/GamePausedOverlay";
import usePlayVisibilityPause from "../common/hooks/usePlayVisibilityPause";
import SocialShareDropdown, { buildShareUrl } from "../common/components/SocialShareDropdown";
import CanvasBoard from "./CanvasBoard";
import BoardMatColorSwitcher from "./BoardMatColorSwitcher";
import AdminColorBasketPlayPanel from "../../../admin/jigsaw/play/AdminColorBasketPlayPanel";
import JigsawColorBasketOverlay from "./JigsawColorBasketOverlay";
import JigsawProgressGift from "./JigsawProgressGift";
import JigsawColorBasketPlayService from "./JigsawColorBasketPlayService";
import { reportColorBasketsUnavailable } from "./colorBasketsUnavailable";
import JigsawDieLineOverlay from "./JigsawDieLineOverlay";
import { useBoardMatColor } from "./useBoardMatColor";
import { buildScatterPositions } from "./CanvasScattered";
import PieceTrayScrollColumn from "./PieceTrayScrollColumn";
import { BOARD_SIZE, PieceVisual, pieceVisualCellOffsetPx, pieceVisualRenderSizePx } from "./PieceVisual";
import PuzzleSolvedOriginalImage from "../common/components/PuzzleSolvedOriginalImage";
import SuccessCard from "../common/components/SuccessCard";
import FeedbackService from "../common/feedback/FeedbackService";
import { usePagesTranslation } from "../../locales";
import usePlayRecord from "../common/hooks/usePlayRecord";
import { saveJigsawPrefsFromTemplate } from "../common/puzzlePlayPrefs";
import PlayBoardSeoHead from "../../components/PlayBoardSeoHead";
import SlidingPlayMistBackground from "../sliding/SlidingPlayMistBackground";
import { SITE_BG, TEXT } from "../../../dark-theme/palette";
import { panelSurfaceSx } from "../../../dark-theme/surfaces";
import {
    applyHomeSnap,
    didDroppedPieceLandAtHome,
    findJigsawLineMatchCelebration,
    moveBoardPieceGroup,
    placeBoardPieceWithAttach,
    snapMovedPiecesToHome,
    tryAttachNeighbors,
} from "./jigsawPieceGroups";
import { PIECE_SHINE_CLEANUP_MS } from "./PieceLockShineOverlay";
import { LINE_COMPLETE_BANNER_MS } from "../sliding/SlidingLineCompleteFx";

function getImageUrl(story) {
    if (!story) return null;
    return story.imageUrl ?? story.imagePreviewUrl ?? story.imageIcon400Url ?? null;
}

function getStoryTitle(story) {
    if (!story) return "";
    return story.english?.title ?? story.en?.title ?? story.french?.title ?? story.fr?.title ?? "";
}

function newBoardPieceId() {
    return `bp${Date.now().toString(36)}${Math.random().toString(36).slice(2, 9)}`;
}

const LAYOUT_MODE_KEY = "jigsaw.layoutMode";
const REFERENCE_ON_SCREEN_KEY = "jigsaw.referenceOnScreen";
const BOARD_ZOOM_KEY = "jigsaw.boardZoom";
/** Toolbar zoom steps — 1 is the viewport-fitted board. */
const BOARD_ZOOM_LEVELS = [0.7, 0.85, 1, 1.25, 1.5, 1.75, 2];
const BOARD_ZOOM_MIN = BOARD_ZOOM_LEVELS[0];
const BOARD_ZOOM_MAX = BOARD_ZOOM_LEVELS[BOARD_ZOOM_LEVELS.length - 1];

/** Reference preview zoom — scales the picture inside a fixed frame. */
const REFERENCE_IMAGE_ZOOM_MIN = 1;
const REFERENCE_IMAGE_ZOOM_MAX = 2.5;
const REFERENCE_IMAGE_ZOOM_STEP = 0.25;
const referencePanelSx = {
    width: "100%",
    aspectRatio: "1 / 1",
    borderRadius: 1,
    overflow: "hidden",
    bgcolor: "grey.900",
    boxShadow: 2,
    position: "relative",
    flexShrink: 0,
};

/** Same icon sizing as sliding puzzle play toolbar — readable on mist backgrounds. */
const PLAY_TOOLBAR_ICON_BTN_SIZING_SX = {
    padding: "6px",
    minWidth: 40,
    minHeight: 40,
    "& .MuiSvgIcon-root": { fontSize: "1.35rem" },
};

/** Light mint on the dark frosted toolbar — readable in the night theme. */
const PLAY_TOOLBAR_FG = TEXT.primary;

/** Raised tray plate over the board — slate so it sits on the night backdrop. */
const TRAY_PLATE_BG = "rgba(15, 23, 42, 0.88)";

/** 16:9 capture play — larger touch targets / readable icons. */
const CAPTURE_PLAY_TOOLBAR_ICON_BTN_SIZING_SX = {
    minWidth: 48,
    minHeight: 48,
    width: 48,
    height: 48,
    padding: "10px",
    boxSizing: "border-box",
    "& .MuiSvgIcon-root": { fontSize: "1.75rem" },
};

/** Apple-size promo window (≈1290×2796) — oversized chrome for video capture. */
const PROMO_PORTRAIT_TOOLBAR_ICON_BTN_SIZING_SX = {
    minWidth: 72,
    minHeight: 72,
    width: 72,
    height: 72,
    padding: "14px",
    boxSizing: "border-box",
    // Override MUI fontSize="small" on nested icons for capture readability.
    "& .MuiSvgIcon-root": { fontSize: "2.35rem !important" },
};

/** Tall portrait capture popup (App Store preview ≈1290×2796; allow browser chrome). */
const PROMO_PORTRAIT_MQ =
    "(min-width: 1000px) and (min-height: 2000px) and (orientation: portrait)";
/** Tray piece boost vs board scale on promo portrait capture. */
const PROMO_PORTRAIT_TRAY_SCALE_BOOST = 1.35;
const PROMO_PORTRAIT_TRAY_ROWS = 2;

/** Fixed OBS / prototype capture canvas — only size this page supports. */
const CAPTURE_FRAME_W = 1920;
const CAPTURE_FRAME_H = 1080;
/** Gap between board and right tray in 16:9 capture layout. */
const CAPTURE_TRAY_GAP_PX = 12;
/** Extra pad beside vertical tray column. */
const CAPTURE_TRAY_EDGE_PAD_PX = 4;
/** Fixed toolbar chrome for 1920×1080 capture (do not measure — OBS CEF lies). */
const CAPTURE_PLAY_CHROME_PX = 56;
/** Outer inset around capture play surface. */
const CAPTURE_PLAY_PAD_PX = 8;
/** Extra gap under toolbar inside the capture frame. */
const CAPTURE_TOOLBAR_GAP_PX = 4;

/** True on the dedicated 1080p recording board route (not the service play page). */
function isJigsawCapture1080Path(pathname) {
    return typeof pathname === "string" && pathname.includes("/recording/1080p/") && pathname.includes("/board/");
}

/** Fill parent — site header hidden on jigsaw board route. Avoid 100vh (OBS CEF misreports vh). */
const JIGSAW_PLAY_VIEWPORT_HEIGHT = "100%";
/** List-tray pieces render slightly smaller than board scale (fits tighter circles). */
const LIST_TRAY_PIECE_DISPLAY_SCALE = 0.88;
/** Padding between piece edge and circle clip (list tray only). */
const LIST_TRAY_BEVEL_BLEED_PX = 1;
/** Extra strip height below circles. */
const LIST_TRAY_STRIP_SLACK_PX = 8;
/** Room for the horizontal scrollbar inside the tray scroller (Windows ~12–17px). */
const LIST_TRAY_SCROLLBAR_RESERVE_PX = 16;

/**
 * Capture tray uses full board piece scale (same screen size as home cells).
 * listTrayMetrics multiplies by LIST_TRAY_PIECE_DISPLAY_SCALE — cancel that for 1:1.
 */
function captureTrayBoardScaleArg(boardSidePx) {
    if (!(boardSidePx > 0) || !(BOARD_SIZE > 0)) return 1 / LIST_TRAY_PIECE_DISPLAY_SCALE;
    return boardSidePx / BOARD_SIZE / LIST_TRAY_PIECE_DISPLAY_SCALE;
}

/** Board side for fixed 1920×1080 capture — pure math, no window / getBoundingClientRect. */
function computeCapture1080BoardSidePx(gridN) {
    const availH = Math.max(
        0,
        CAPTURE_FRAME_H - CAPTURE_PLAY_CHROME_PX - CAPTURE_TOOLBAR_GAP_PX - CAPTURE_PLAY_PAD_PX * 2,
    );
    const availW = Math.max(0, CAPTURE_FRAME_W - CAPTURE_PLAY_PAD_PX * 2);
    let boardSide = Math.floor(availH);
    if (!(gridN > 0) || boardSide < 20) {
        return Math.floor(Math.min(availH, availW));
    }
    for (let i = 0; i < 3; i += 1) {
        const trayCol =
            listTrayMetrics(BOARD_SIZE / gridN, captureTrayBoardScaleArg(boardSide)).circleOuterPx +
            CAPTURE_TRAY_EDGE_PAD_PX +
            CAPTURE_TRAY_GAP_PX;
        boardSide = Math.floor(Math.min(availH, Math.max(0, availW - trayCol)));
    }
    return Math.max(20, boardSide);
}

/** Tray stacks above the board while a placement hint is running. */
const Z_TRAY_HINT_FRONT = 40;

const trayHintPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 2px rgba(59, 166, 102, 0.55), 0 0 8px rgba(59, 166, 102, 0.35);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(59, 166, 102, 0.95), 0 0 16px rgba(59, 166, 102, 0.75);
  }
`;

/** Portrait phone: fit board and tray inside the viewport. */
const MOBILE_PORTRAIT_PLAY_HEIGHT = "100%";
const MOBILE_PORTRAIT_BOARD_MAX_VH = 46;
const MOBILE_PORTRAIT_TRAY_MAX_VH = 24;

/** Toolbar + page padding reserved from viewport when capping board height. */
const DESKTOP_PLAY_CHROME_OFFSET = "4.5rem";
const DESKTOP_PLAY_CHROME_PX = 72;
const DESKTOP_PLAY_PAD_PX = 16;

/** Tray / unplaced pieces stack above the board area. */
const Z_TRAY_AREA = 2;

const equalSplitSquareSx = {
    aspectRatio: "1 / 1",
    width: "100%",
    maxWidth: "100%",
    maxHeight: "100%",
    minWidth: 0,
    flexShrink: 0,
    overflow: "visible",
    boxSizing: "border-box",
};

/** Explicit width + height for matched split squares (3/3). */
function explicitPlaySquareSx(sidePx, maxHeight) {
    if (!sidePx || sidePx <= 0) {
        return { width: "100%", minWidth: 0, maxWidth: "100%", aspectRatio: "1 / 1", maxHeight };
    }
    return {
        width: sidePx,
        height: sidePx,
        maxWidth: "100%",
        minWidth: 0,
        flexShrink: 0,
        overflow: "visible",
        boxSizing: "border-box",
    };
}

function isEdgePieceIndex(pieceIndex, gridN) {
    const n = Number(gridN) || 0;
    const idx = Number(pieceIndex);
    if (!Number.isFinite(idx) || n <= 1) return true;
    const row = Math.floor(idx / n);
    const col = idx % n;
    return row === 0 || col === 0 || row === n - 1 || col === n - 1;
}

/**
 * One-time tray shuffle — order must not change when pieces move to the board.
 * All pieces are shuffled, then edge pieces are moved to the front (still shuffled among themselves).
 */
function shuffleTrayPieceIndices(pieceIndices, gridN, templateKey = "") {
    const arr = [...pieceIndices];
    let h = 0;
    const key = String(templateKey ?? "");
    for (let c = 0; c < key.length; c += 1) h = (h * 31 + key.charCodeAt(c)) | 0;
    const seed = gridN * 31 + arr.length * 97 + h;
    for (let i = arr.length - 1; i > 0; i -= 1) {
        const x = Math.sin((seed + i * 7919 + arr[i] * 104729) * 12.9898 + 78.233) * 43758.5453;
        const j = Math.floor((x - Math.floor(x)) * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    const edges = [];
    const interior = [];
    for (const idx of arr) {
        if (isEdgePieceIndex(idx, gridN)) {
            edges.push(idx);
        } else {
            interior.push(idx);
        }
    }
    return [...edges, ...interior];
}

/** Screen pixels for a board piece (cell size × board scale). */
function listTrayPieceScreenPx(cellPx, scale) {
    return pieceVisualRenderSizePx(cellPx) * scale;
}

/** Board cell origin (anchorToCell) from list-tray visual top-left at drop position. */
function listTrayDropCellOrigin(circleLeft, circleTop, circleSizePx, boardRect, cellPx) {
    const { x: visualX, y: visualY } = listTrayPieceBoardTopLeft(
        circleLeft,
        circleTop,
        circleSizePx,
        boardRect,
        cellPx,
    );
    const tabOffset = pieceVisualCellOffsetPx(cellPx);
    return { x: visualX + tabOffset, y: visualY + tabOffset };
}

/** Visual top-left for a piece centered in a list-tray circle at screen coords. */
function listTrayPieceBoardTopLeft(circleLeft, circleTop, circleSizePx, boardRect, cellPx) {
    const s = boardRect.width / BOARD_SIZE || 1;
    const pieceScreen = listTrayPieceScreenPx(cellPx, s);
    const pieceLeft = circleLeft + (circleSizePx - pieceScreen) / 2;
    const pieceTop = circleTop + (circleSizePx - pieceScreen) / 2;
    return {
        x: (pieceLeft - boardRect.left) / s,
        y: (pieceTop - boardRect.top) / s,
        s,
    };
}

/** List-tray circle + piece sizing (shared by layout and drag logic). */
function listTrayMetrics(cellPx, boardScale) {
    const trayScale = boardScale * LIST_TRAY_PIECE_DISPLAY_SCALE;
    const pieceScreenPx = Math.round(pieceVisualRenderSizePx(cellPx) * trayScale);
    const circleOuterPx = pieceScreenPx + LIST_TRAY_BEVEL_BLEED_PX * 2;
    const stripHeightPx =
        circleOuterPx + LIST_TRAY_STRIP_SLACK_PX + LIST_TRAY_SCROLLBAR_RESERVE_PX;
    return { pieceScreenPx, circleOuterPx, stripHeightPx, trayScale };
}

/** Tray strip wrappers — must not clip the piece circles. */
const listTraySectionSx = {
    flexShrink: 0,
    width: "100%",
    overflow: "visible",
    position: "relative",
    zIndex: Z_TRAY_AREA,
};

function getDragGhostBasePosition(session, cellPx) {
    const scale = session.ghostScale ?? 1;
    let left = session.elLeft;
    let top = session.elTop;
    if (session.fromScattered && session.listTray) {
        const pieceScreen = listTrayPieceScreenPx(cellPx, scale);
        left += (session.elWidth - pieceScreen) / 2;
        top += (session.elHeight - pieceScreen) / 2;
    }
    return { left, top, scale };
}

/** Move drag ghost via compositor — avoids React re-renders on every pointer move. */
function applyDragGhostTransform(ghostEl, session, clientX, clientY) {
    if (!ghostEl || !session) return;
    const deltaX = clientX - session.startX;
    const deltaY = clientY - session.startY;
    const scale = session.ghostScale ?? 1;
    ghostEl.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${scale})`;
}

function loadReferenceOnScreen() {
    try {
        const v = localStorage.getItem(REFERENCE_ON_SCREEN_KEY);
        if (v === "0" || v === "false") return false;
        if (v === "1" || v === "true") return true;
        const legacy = localStorage.getItem(LAYOUT_MODE_KEY);
        if (legacy === "hide") return false;
        if (legacy === "split" || legacy === "narrow") return true;
    } catch {}
    return false;
}

function persistReferenceOnScreen(onScreen) {
    try {
        localStorage.setItem(REFERENCE_ON_SCREEN_KEY, onScreen ? "1" : "0");
    } catch {
        /* ignore */
    }
}

function clampBoardZoom(zoom) {
    if (!Number.isFinite(zoom) || zoom <= 0) return 1;
    return Math.min(BOARD_ZOOM_MAX, Math.max(BOARD_ZOOM_MIN, zoom));
}

function loadBoardZoom() {
    try {
        const raw = localStorage.getItem(BOARD_ZOOM_KEY);
        if (raw != null) return clampBoardZoom(Number(raw));
    } catch {}
    return 1;
}

function persistBoardZoom(zoom) {
    try {
        localStorage.setItem(BOARD_ZOOM_KEY, String(zoom));
    } catch {
        /* ignore */
    }
}

/** Next zoom step (direction 1 = in, -1 = out); returns current at the limits. */
function stepBoardZoom(current, direction) {
    const eps = 0.001;
    if (direction > 0) {
        return BOARD_ZOOM_LEVELS.find((level) => level > current + eps) ?? current;
    }
    const lower = BOARD_ZOOM_LEVELS.filter((level) => level < current - eps);
    return lower.length > 0 ? lower[lower.length - 1] : current;
}

export default function JigsawPlayBoardPage() {
    const { storyId, jigsawId, basketIndex: basketIndexParam } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    /** Dedicated 1920×1080 recording board — not the service `/play/...` page. */
    const isCaptureLandscape = isJigsawCapture1080Path(location.pathname);
    const promoQuery =
        searchParams.get("promo") === "1" ||
        searchParams.get("capture") === "apple";
    const promoPortraitMq = useMediaQuery(PROMO_PORTRAIT_MQ);
    const [promoByViewport, setPromoByViewport] = useState(false);
    useEffect(() => {
        const sync = () => {
            if (typeof window === "undefined") return;
            const w = window.innerWidth;
            const h = window.innerHeight;
            // 1290×2796 capture window (allow browser chrome / DPI rounding).
            setPromoByViewport(w >= 1000 && h >= 1800 && h > w * 1.4);
        };
        sync();
        window.addEventListener("resize", sync);
        return () => window.removeEventListener("resize", sync);
    }, []);
    /** Real play opened in a 1290×2796-class window (or ?promo=1) for trailer capture. */
    const isPromoPortraitCapture =
        !isCaptureLandscape && (promoQuery || promoPortraitMq || promoByViewport);
    const toolbarIconSx = isCaptureLandscape
        ? { color: PLAY_TOOLBAR_FG, ...CAPTURE_PLAY_TOOLBAR_ICON_BTN_SIZING_SX }
        : isPromoPortraitCapture
          ? { color: PLAY_TOOLBAR_FG, ...PROMO_PORTRAIT_TOOLBAR_ICON_BTN_SIZING_SX }
          : { color: PLAY_TOOLBAR_FG, ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX };

    const colorBasketIndex = Number(basketIndexParam);
    const colorBasketMode =
        Number.isInteger(colorBasketIndex) && colorBasketIndex >= 0 && Boolean(basketIndexParam);
    const colorBasketCountParam = Number(searchParams.get("basketCount"));
    /** Set by the basket practice panel; awaiting it persists its pending saves. */
    const colorBasketFlushRef = useRef(null);

    const handleBackFromBoard = async () => {
        if (colorBasketMode && storyId && jigsawId) {
            // Flush basket practice first: backend mirrors home pieces onto main, and the
            // reload below must see them.
            try {
                await colorBasketFlushRef.current?.();
            } catch (error) {
                Logger.warn("Color basket flush before leaving failed", error);
            }
            navigate(
                `/puzzle/play-jigsaw/${encodeURIComponent(storyId)}/board/${encodeURIComponent(jigsawId)}`,
                { state: location.state },
            );
            return;
        }
        if (typeof location.state?.prototypeReturnPath === "string") {
            navigate(location.state.prototypeReturnPath);
            return;
        }
        if (location.state?.fromLibrary === true) {
            navigate("/account/library", {
                state: { activeTab: location.state?.activeTab ?? 0 },
            });
            return;
        }
        navigate(`/puzzle/play-jigsaw/${storyId}`, { state: location.state });
    };

    usePlayRecord({
        puzzleType: "JIGSAW",
        puzzleId: jigsawId,
        enabled: Boolean(jigsawId),
    });

    useEffect(() => {
        trackEvent("entered", {
            name: "JigsawPlayBoardPage",
            puzzle_type: "JIGSAW",
            page_kind: "play",
            story_id: storyId ?? undefined,
            puzzle_id: jigsawId ?? undefined,
        });
    }, [storyId, jigsawId]);

    const { t } = usePagesTranslation();
    const { triggerConfetti } = useConfetti();
    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [boardPiecesMain, setBoardPiecesMain] = useState([]);
    const [boardPiecesScatter, setBoardPiecesScatter] = useState([]);
    const [scatterPositions, setScatterPositions] = useState({});
    const [colorBasketOverlayOpen, setColorBasketOverlayOpen] = useState(false);
    const [colorBasketsAvailable, setColorBasketsAvailable] = useState(false);
    const [colorBasketSession, setColorBasketSession] = useState({
        loading: false,
        error: "",
        baskets: [],
        basketCount: null,
    });
    const [dragging, setDragging] = useState(null);
    /** Last-selected order for overlapping unplaced pieces (pieceIndex → stack rank). */
    const [pieceFocusOrder, setPieceFocusOrder] = useState({});
    const pieceFocusCounterRef = useRef(0);
    /** Brief shine overlays on correct placement — removed from DOM after cleanup. */
    const [activePieceShines, setActivePieceShines] = useState([]);
    const shineCleanupTimersRef = useRef([]);
    const [lineMatchFx, setLineMatchFx] = useState(null);
    const lineMatchFxClearRef = useRef(null);
    const lineMatchFxIdRef = useRef(0);

    const triggerPieceShine = useCallback((boardId, pieceIndex) => {
        if (!boardId || pieceIndex == null) return;
        const addition = {
            boardId,
            pieceIndex,
            id: `${boardId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        };
        setActivePieceShines((prev) => [...prev, addition]);
        const timer = window.setTimeout(() => {
            setActivePieceShines((prev) => prev.filter((entry) => entry.id !== addition.id));
        }, PIECE_SHINE_CLEANUP_MS);
        shineCleanupTimersRef.current.push(timer);
    }, []);

    const clearLineMatchFx = useCallback((id) => {
        setLineMatchFx((cur) => (cur?.id === id ? null : cur));
    }, []);

    const pickLinePraise = useCallback(() => {
        const keys = [
            "puzzle.sliding.praise-row-complete",
            "puzzle.sliding.praise-good-job",
            "puzzle.sliding.praise-great",
            "puzzle.sliding.praise-excellent",
        ];
        const fallbacks = ["Row Complete!", "Good Job!", "Great!", "Excellent!"];
        const i = Math.floor(Math.random() * keys.length);
        const translated = t(keys[i]);
        return translated === keys[i] ? fallbacks[i] : translated;
    }, [t]);

    useEffect(
        () => () => {
            for (const timer of shineCleanupTimersRef.current) {
                window.clearTimeout(timer);
            }
            shineCleanupTimersRef.current = [];
            if (lineMatchFxClearRef.current) {
                window.clearTimeout(lineMatchFxClearRef.current);
            }
        },
        [],
    );

    const celebrateCorrectPlacement = useCallback(
        (prev, next, droppedPieceIndex, gridN, cellPxN) => {
            const landedHome = didDroppedPieceLandAtHome(
                prev,
                next,
                droppedPieceIndex,
                gridN,
                cellPxN,
            );
            if (landedHome) {
                const placed = next.find((b) => b.pieceIndex === droppedPieceIndex);
                if (placed) {
                    if (soundEffectsEnabledRef.current) {
                        AudioService.play(AUDIO_EFFECT.JIGSAW_PIECE_SNAP);
                    }
                    triggerPieceShine(placed.id, droppedPieceIndex);
                }
            }

            const found = findJigsawLineMatchCelebration(prev, next, gridN, cellPxN);
            if (!found) return;

            // Shine every piece of the completed row/column, not only the piece just dropped.
            const lineCells = new Set(found.cells);
            for (const piece of next) {
                if (!lineCells.has(piece.pieceIndex)) continue;
                if (landedHome && piece.pieceIndex === droppedPieceIndex) continue;
                triggerPieceShine(piece.id, piece.pieceIndex);
            }

            if (lineMatchFxClearRef.current) {
                window.clearTimeout(lineMatchFxClearRef.current);
            }
            lineMatchFxIdRef.current += 1;
            const id = lineMatchFxIdRef.current;
            setLineMatchFx({
                id,
                rows: found.rows,
                cols: found.cols,
                praise: pickLinePraise(),
            });
            if (soundEffectsEnabledRef.current) {
                void AudioService.play(AUDIO_EFFECT.SLIDING_LINE_MATCH);
            }
            lineMatchFxClearRef.current = window.setTimeout(() => {
                clearLineMatchFx(id);
            }, LINE_COMPLETE_BANNER_MS + 50);
        },
        [clearLineMatchFx, pickLinePraise, triggerPieceShine],
    );

    // Play session state
    const [playSession, setPlaySession] = useState(null);
    const [sessionLoading, setSessionLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successRating, setSuccessRating] = useState(0);
    const [successRatingBusy, setSuccessRatingBusy] = useState(false);
    const [successStats, setSuccessStats] = useState({ time: "0:00", accuracy: 100 });
    const [successSpeedRank, setSuccessSpeedRank] = useState(null);

    const boardRefMain = useRef(null);
    const boardRefScatter = useRef(null);
    const trayRef = useRef(null);
    const scatteredRef = useRef(null);
    const puzzleColumnRef = useRef(null);
    const playSurfaceRef = useRef(null);
    const playRootRef = useRef(null);
    const captureToolbarRef = useRef(null);
    const dragGhostRef = useRef(null);
    const [cloudUserId, setCloudUserId] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    /** null | pending | ok | insufficient | error — premium board credit charge */
    const [premiumCreditStatus, setPremiumCreditStatus] = useState(null);
    const [premiumCreditError, setPremiumCreditError] = useState("");
    const [creditAlertOpen, setCreditAlertOpen] = useState(false);
    const [creditAlertMessage, setCreditAlertMessage] = useState("");
    const [creditAlertInsufficient, setCreditAlertInsufficient] = useState(false);
    const [basketInsufficientCoinsOpen, setBasketInsufficientCoinsOpen] = useState(false);
    const [basketUnlockCoinCost, setBasketUnlockCoinCost] = useState(5);
    const [bgmAutoPlay, setBgmAutoPlay] = useState(false);
    const [audioPrefsReady, setAudioPrefsReady] = useState(false);
    const [referenceOnScreen, setReferenceOnScreen] = useState(loadReferenceOnScreen);
    const [referenceImageZoom, setReferenceImageZoom] = useState(1);
    const referenceClipRef = useRef(null);
    const referenceImageRef = useRef(null);
    const referencePanRef = useRef({ x: 0, y: 0 });
    const referenceZoomRef = useRef(referenceImageZoom);
    referenceZoomRef.current = referenceImageZoom;
    const [pieceViewMode, setPieceViewMode] = useState("list"); // "list" or "scattered"
    const [boardScale, setBoardScale] = useState(1);
    const [boardZoomPref, setBoardZoomPref] = useState(loadBoardZoom);
    const [boardSquarePx, setBoardSquarePx] = useState(null);
    const [playBoardSquarePx, setPlayBoardSquarePx] = useState(0);
    const [playSurfaceBox, setPlaySurfaceBox] = useState({ w: 0, h: 0 });
    const [trayBesideBoard, setTrayBesideBoard] = useState(false);
    const [docViewportPx, setDocViewportPx] = useState({ w: 0, h: 0 });
    const boardSquarePxRef = useRef(null);
    boardSquarePxRef.current = boardSquarePx;
    /** Capture routes paint at fixed sizes — toolbar zoom applies to real play only. */
    const boardZoom = isCaptureLandscape || isPromoPortraitCapture ? 1 : boardZoomPref;
    const canZoomIn = boardZoom < BOARD_ZOOM_MAX - 0.001;
    const canZoomOut = boardZoom > BOARD_ZOOM_MIN + 0.001;

    const handleBoardZoomStep = useCallback((direction) => {
        setBoardZoomPref((prev) => {
            const next = clampBoardZoom(stepBoardZoom(prev, direction));
            if (next === prev) return prev;
            persistBoardZoom(next);
            return next;
        });
    }, []);

    const handleBoardZoomReset = useCallback(() => {
        setBoardZoomPref((prev) => {
            if (prev === 1) return prev;
            persistBoardZoom(1);
            return 1;
        });
    }, []);

    const wheelZoomAccumRef = useRef(0);
    const wheelZoomLockRef = useRef(0);

    // A zoomed-in board scrolls inside the fitted frame — stay on the middle of the board.
    useLayoutEffect(() => {
        const el = puzzleColumnRef.current;
        if (!el || boardZoom <= 1) return;
        el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
        el.scrollTop = Math.max(0, (el.scrollHeight - el.clientHeight) / 2);
    }, [boardZoom]);
    const { mat: boardMat, matId: boardMatId, setMatId: setBoardMatId, grooveStyle: boardGrooveStyle } =
        useBoardMatColor();
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [showDieLines, setShowDieLines] = useState(true);
    const [settingsAnchor, setSettingsAnchor] = useState(null);
    const [walletPlayCredits, setWalletPlayCredits] = useState(null);
    const [walletHintCredits, setWalletHintCredits] = useState(null);
    const [walletPuzzleCoins, setWalletPuzzleCoins] = useState(null);
    const [walletBalancesLoading, setWalletBalancesLoading] = useState(false);
    const [placementHintBusy, setPlacementHintBusy] = useState(false);
    const [placementHintMessage, setPlacementHintMessage] = useState("");
    const [placementHintMessageSeverity, setPlacementHintMessageSeverity] = useState("warning");
    /** Seconds left until board hint clears; null when idle. */
    const [placementHintCountdownSec, setPlacementHintCountdownSec] = useState(null);
    const [placementHintCountdownActive, setPlacementHintCountdownActive] = useState(false);
    /** Epoch ms when the board-hint countdown reaches 0 (stable tick source). */
    const placementHintEndsAtRef = useRef(null);
    /** null | { pieceIndices: number[], mode: "pick"|"step"|"all", stepIndex: number|null } */
    const [placementHint, setPlacementHint] = useState(null);
    const placementHintRunIdRef = useRef(0);
    const [bgmPlaying, setBgmPlaying] = useState(false);
    const [soundEffectsEnabled, setSoundEffectsEnabled] = useState(false);
    const [timerPaused, setTimerPaused] = useState(false);
    const bgmButtonWrapRef = useRef(null);
    const soundEffectsEnabledRef = useRef(true);
    const timerPauseStartedAtRef = useRef(null);
    soundEffectsEnabledRef.current = soundEffectsEnabled;

    useEffect(() => {
        document.documentElement.style.backgroundColor = SITE_BG;
        document.body.style.backgroundColor = SITE_BG;
        return () => {
            document.documentElement.style.backgroundColor = "";
            document.body.style.backgroundColor = "";
        };
    }, []);

    useEffect(() => {
        UserService.getUserInfo()
            .then((data) => {
                setUserInfo(data);
                setCloudUserId(data?.cloudUser?.id ?? null);
                setBgmAutoPlay(false);
                setBgmPlaying(false);
                setSoundEffectsEnabled(false);
            })
            .catch(() => {
                setUserInfo(null);
            })
            .finally(() => setAudioPrefsReady(true));
    }, []);
    const hasInitializedRef = useRef(false);
    const playStartMsRef = useRef(null);
    const moveCountRef = useRef(0);
    const successShownRef = useRef(false);

    /** Grid dimension — prefer T09-style name over wrong API tileSize (see jigsawBrowseUtils). */
    const puzzleGrid = useMemo(() => {
        if (!selectedTemplate) return 0;
        const fromTemplate = resolveJigsawTemplateTileSize(selectedTemplate);
        if (fromTemplate > 0) return fromTemplate;
        const pieces = selectedTemplate.piecePaths?.pieces;
        if (Array.isArray(pieces) && pieces.length > 0) {
            const n = Math.round(Math.sqrt(pieces.length));
            if (n * n === pieces.length) return n;
        }
        const direct = Number(selectedTemplate.tileSize);
        return Number.isFinite(direct) && direct > 0 ? direct : 0;
    }, [selectedTemplate]);

    // Calculate solved state for completion checking
    const isSolvedScatter = useMemo(() => {
        if (!selectedTemplate || puzzleGrid <= 0) return false;
        const grid = puzzleGrid;
        const total = grid * grid;
        const cellPx = BOARD_SIZE / grid;
        const tol = cellPx * 0.22;

        return (
            boardPiecesScatter.length === total &&
            boardPiecesScatter.every((b) => {
                const hx = (b.pieceIndex % grid) * cellPx;
                const hy = Math.floor(b.pieceIndex / grid) * cellPx;
                return Math.abs(b.x - hx) < tol && Math.abs(b.y - hy) < tol;
            })
        );
    }, [selectedTemplate, puzzleGrid, boardPiecesScatter]);

    const { visibilityPaused, continuePlaying } = usePlayVisibilityPause({
        enabled: Boolean(selectedTemplate) && puzzleGrid > 0 && !isSolvedScatter,
    });
    const timerEffectivelyPaused = timerPaused || visibilityPaused;

    // Load group data and find matching template
    useEffect(() => {
        if (!storyId) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const data = await ApiService.getData(`/home/jigsaw/${encodeURIComponent(storyId)}`);
                const normalized = normalizeHomeJigsawGroup(data) ?? data;
                if (!cancelled) {
                    setGroup(normalized);

                    if (isJigsawGroupUnavailable(normalized)) {
                        navigate(`/puzzle/play-jigsaw/${encodeURIComponent(storyId)}`, {
                            replace: true,
                            state: location.state,
                        });
                        return;
                    }

                    if (normalized?.templates?.length && jigsawId) {
                        const found = normalized.templates.find((t) => t.id === jigsawId);
                        if (found) {
                            setSelectedTemplate(found);
                        } else {
                            // Stale board id (e.g. old Facebook share) → story select still works.
                            navigate(`/puzzle/play-jigsaw/${encodeURIComponent(storyId)}`, {
                                replace: true,
                                state: location.state,
                            });
                            return;
                        }
                    }
                }
            } catch (e) {
                Logger.error("JigsawPlayBoardPage:", e);
            }
            if (!cancelled) setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [storyId, jigsawId, navigate, location.state]);

    useEffect(() => {
        if (!cloudUserId || !selectedTemplate) return;
        saveJigsawPrefsFromTemplate(cloudUserId, selectedTemplate);
    }, [cloudUserId, selectedTemplate]);

    useEffect(() => {
        if (selectedTemplate?.id) {
            setBoardPiecesMain([]);
            setBoardPiecesScatter([]);
            setPieceFocusOrder({});
            pieceFocusCounterRef.current = 0;
            setShowSuccess(false);
            setSuccessSpeedRank(null);
            setLineMatchFx(null);
            setTimerPaused(false);
            timerPauseStartedAtRef.current = null;
            hasInitializedRef.current = false;
            successShownRef.current = false;
            moveCountRef.current = 0;
            playStartMsRef.current = null;
            clearPlayElapsedTimerPersist(`jigsaw-play-elapsed:${selectedTemplate.id}`);
            const n = puzzleGrid;
            const tot = n * n;
            const key = selectedTemplate.id ?? selectedTemplate.jigsawTemplateId ?? String(n);
            setScatterPositions(buildScatterPositions(tot, n, key));
        }
    }, [selectedTemplate?.id, puzzleGrid]);

    // Start play session when template is selected
    const startPlaySession = useCallback(async () => {
        if (!selectedTemplate?.id) return;

        setSessionLoading(true);
        try {
            const n = puzzleGrid || resolveJigsawTemplateTileSize(selectedTemplate);
            const tot = n * n;
            const key = selectedTemplate.id ?? selectedTemplate.jigsawTemplateId ?? String(n);
            const initialScatterPositions = buildScatterPositions(tot, n, key);

            // Omit layout on resume so an in-progress session is not reset; only
            // send placeholders when the backend must create a brand-new row.
            const result = await JigsawPlayService.start({
                jigsawId: selectedTemplate.id,
                scatteredPieces: initialScatterPositions,
                boardPositions: [],
            });

            if (result.success) {
                setPlaySession(result.data);
                Logger.info('Play session started:', result.data);

                const board = Array.isArray(result.data.boardPositions)
                    ? result.data.boardPositions
                    : [];
                setBoardPiecesScatter(board);
                const onBoard = new Set(
                    board
                        .map((b) => Number(b?.pieceIndex))
                        .filter((n) => Number.isInteger(n) && n >= 0),
                );
                if (result.data.scatteredPieces && typeof result.data.scatteredPieces === "object") {
                    const nextScatter = { ...result.data.scatteredPieces };
                    for (const idx of onBoard) {
                        delete nextScatter[idx];
                        delete nextScatter[String(idx)];
                    }
                    setScatterPositions(nextScatter);
                } else {
                    const fallback = { ...initialScatterPositions };
                    for (const idx of onBoard) {
                        delete fallback[idx];
                    }
                    setScatterPositions(fallback);
                }
            } else {
                Logger.error('Failed to start play session:', result.error);
            }
        } catch (error) {
            Logger.error('Error starting play session:', error);
        } finally {
            setSessionLoading(false);
        }
    }, [selectedTemplate, puzzleGrid]);

    // Color baskets available for this board (toolbar button gate).
    useEffect(() => {
        let cancelled = false;
        const id = selectedTemplate?.id;
        if (!id || puzzleGrid < 9) {
            setColorBasketsAvailable(false);
            return undefined;
        }
        (async () => {
            try {
                const meta = await JigsawColorBasketPlayService.getColorBaskets(id);
                if (cancelled) return;
                setColorBasketsAvailable(meta?.available !== false);
            } catch {
                if (!cancelled) setColorBasketsAvailable(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedTemplate?.id, puzzleGrid]);

    const handleColorBasketOverlayClose = useCallback(() => {
        setColorBasketOverlayOpen(false);
    }, []);

    // Load color-basket session when viewing /basket/:index under the play toolbar.
    useEffect(() => {
        if (!colorBasketMode || !jigsawId) {
            setColorBasketSession({ loading: false, error: "", baskets: [], basketCount: null });
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setColorBasketSession((prev) => ({ ...prev, loading: true, error: "" }));
            try {
                const meta = await JigsawColorBasketPlayService.getColorBaskets(jigsawId);
                if (cancelled) return;
                if (meta?.available === false) {
                    const reason = reportColorBasketsUnavailable(meta, "JigsawPlayBoardPage");
                    setColorBasketSession({
                        loading: false,
                        error: t("puzzle.jigsaw.baskets-unavailable", {
                            reason,
                            defaultValue: "Color baskets unavailable: {{reason}}",
                        }),
                        baskets: [],
                        basketCount: null,
                    });
                    return;
                }
                const allowed =
                    Array.isArray(meta?.allowedCounts) && meta.allowedCounts.length > 0
                        ? meta.allowedCounts.map(Number)
                        : [];
                let k =
                    Number.isInteger(colorBasketCountParam) && colorBasketCountParam >= 2
                        ? colorBasketCountParam
                        : Number(meta?.freeMaxCount);
                if (!(Number.isInteger(k) && k >= 2)) {
                    k = allowed[allowed.length - 1] || Number(meta?.maxBasketCount) || 2;
                }
                if (allowed.length > 0 && !allowed.includes(k)) {
                    k = allowed.includes(Number(meta?.freeMaxCount))
                        ? Number(meta.freeMaxCount)
                        : allowed[allowed.length - 1];
                }
                const result = await JigsawColorBasketPlayService.getColorBaskets(jigsawId, k);
                if (cancelled) return;
                if (result?.available === false) {
                    const reason = reportColorBasketsUnavailable(result, "JigsawPlayBoardPage/baskets");
                    setColorBasketSession({
                        loading: false,
                        error: t("puzzle.jigsaw.baskets-unavailable", {
                            reason,
                            defaultValue: "Color baskets unavailable: {{reason}}",
                        }),
                        baskets: [],
                        basketCount: k,
                    });
                    return;
                }
                let list = Array.isArray(result?.baskets) ? result.baskets : [];
                if (!list.some((b) => Number(b?.index) === colorBasketIndex)) {
                    setColorBasketSession({
                        loading: false,
                        error: t("puzzle.jigsaw.basket-not-found", "Basket not found."),
                        baskets: list,
                        basketCount: k,
                    });
                    return;
                }
                const sessions = await JigsawColorBasketPlayService.listSessions(jigsawId, k);
                if (cancelled) return;
                const deployedSession = Array.isArray(sessions.data)
                    ? sessions.data.find(
                          (s) =>
                              Number(s?.basketIndex) === colorBasketIndex &&
                              String(s?.playState || "").trim().toUpperCase() === "DEPLOYED",
                      )
                    : null;
                if (deployedSession) {
                    setColorBasketSession({
                        loading: false,
                        error: t(
                            "puzzle.jigsaw.basket-already-deployed",
                            "This basket was already deployed and cannot be played again.",
                        ),
                        baskets: list,
                        basketCount: k,
                    });
                    return;
                }
                const selected = list.find((b) => Number(b?.index) === colorBasketIndex);
                const hasSession = Array.isArray(sessions.data)
                    ? sessions.data.some((s) => Number(s?.basketIndex) === colorBasketIndex)
                    : false;
                if (
                    !hasSession &&
                    (selected?.requiresUnlock === true || selected?.unlocked === false)
                ) {
                    try {
                        await JigsawColorBasketPlayService.unlockColorBaskets(
                            jigsawId,
                            k,
                            colorBasketIndex,
                        );
                        list = list.map((b) =>
                            Number(b?.index) === colorBasketIndex
                                ? { ...b, unlocked: true, requiresUnlock: false }
                                : b,
                        );
                    } catch (unlockErr) {
                        if (cancelled) return;
                        const msg = unlockErr?.message || "";
                        const cost = Number(result?.unlockCoinCost) || 5;
                        if (/insufficient/i.test(msg)) {
                            setBasketUnlockCoinCost(cost);
                            setBasketInsufficientCoinsOpen(true);
                            setColorBasketSession({
                                loading: false,
                                error: t("puzzle.jigsaw.basket-unlock-insufficient", {
                                    count: cost,
                                    defaultValue:
                                        "Not enough coins. Each basket costs {{count}} coins.",
                                }),
                                baskets: list,
                                basketCount: k,
                            });
                            return;
                        }
                        setColorBasketSession({
                            loading: false,
                            error: /auth|unauthor|login required|authentication/i.test(msg)
                                ? t(
                                      "puzzle.jigsaw.basket-unlock-need-login",
                                      "Please sign in to unlock color baskets with coins.",
                                  )
                                : msg ||
                                  t(
                                      "puzzle.jigsaw.basket-unlock-failed",
                                      "Could not unlock this basket. Please try again.",
                                  ),
                            baskets: list,
                            basketCount: k,
                        });
                        return;
                    }
                }
                if (cancelled) return;
                setColorBasketSession({
                    loading: false,
                    error: "",
                    baskets: list,
                    basketCount: k,
                });
            } catch (e) {
                Logger.error("JigsawPlayBoardPage color basket:", e);
                if (!cancelled) {
                    const reason = reportColorBasketsUnavailable(
                        { reason: e?.message || "request_failed" },
                        "JigsawPlayBoardPage/error",
                    );
                    setColorBasketSession({
                        loading: false,
                        error: t("puzzle.jigsaw.baskets-unavailable", {
                            reason,
                            defaultValue: "Color baskets unavailable: {{reason}}",
                        }),
                        baskets: [],
                        basketCount: null,
                    });
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [colorBasketMode, jigsawId, colorBasketIndex, colorBasketCountParam, t]);

    // Returning from basket practice: refresh main board (deploy may have updated it).
    const wasColorBasketModeRef = useRef(false);
    useEffect(() => {
        if (wasColorBasketModeRef.current && !colorBasketMode) {
            void startPlaySession();
        }
        wasColorBasketModeRef.current = colorBasketMode;
    }, [colorBasketMode, startPlaySession]);

    const selectedColorBasket = useMemo(() => {
        if (!colorBasketMode) return null;
        return (
            colorBasketSession.baskets.find((b) => Number(b?.index) === colorBasketIndex) ?? null
        );
    }, [colorBasketMode, colorBasketSession.baskets, colorBasketIndex]);

    // Auto-save progress with debouncing
    const saveProgress = useCallback(async (isCompleted = false) => {
        if (!playSession?.id || !selectedTemplate?.id) return null;
        // Basket practice owns main-board layout while open — never overwrite deploy.
        if (colorBasketMode) return null;

        try {
            const result = await JigsawPlayService.progress({
                jigsawId: selectedTemplate.id,
                scatteredPieces: scatterPositions,
                boardPositions: boardPiecesScatter,
                isCompleted
            });

            if (result.success) {
                setPlaySession(result.data);
                Logger.info('Progress saved:', { isCompleted });
            } else {
                Logger.error('Failed to save progress:', result.error);
            }
            return result;
        } catch (error) {
            Logger.error('Error saving progress:', error);
            return null;
        }
    }, [playSession?.id, selectedTemplate?.id, scatterPositions, boardPiecesScatter, colorBasketMode]);

    // Debounced auto-save
    const autoSaveRef = useRef(null);
    const saveProgressRef = useRef(saveProgress);
    saveProgressRef.current = saveProgress;
    const playSessionRef = useRef(playSession);
    playSessionRef.current = playSession;
    const colorBasketModeRef = useRef(colorBasketMode);
    colorBasketModeRef.current = colorBasketMode;

    const scheduleAutoSave = useCallback(() => {
        if (colorBasketModeRef.current) return;
        if (autoSaveRef.current) {
            clearTimeout(autoSaveRef.current);
        }

        autoSaveRef.current = setTimeout(() => {
            if (colorBasketModeRef.current) return;
            saveProgressRef.current(false);
        }, 2000); // Save after 2 seconds of inactivity
    }, []);

    // Suspend main-board autosave for the whole basket practice visit.
    useEffect(() => {
        if (!colorBasketMode) return undefined;
        if (autoSaveRef.current) {
            clearTimeout(autoSaveRef.current);
            autoSaveRef.current = null;
        }
        return undefined;
    }, [colorBasketMode]);

    const applyMainBoardFromBasketSync = useCallback((apiData) => {
        if (!apiData || typeof apiData !== "object") return;
        const raw =
            apiData.mainBoardPositions
            ?? apiData.mainPlay?.boardPositions
            ?? apiData.boardPositions;
        if (!Array.isArray(raw)) return;
        const stamp = Date.now();
        const nextBoard = raw
            .map((b, i) => {
                const pieceIndex = Number(b?.pieceIndex);
                if (!Number.isInteger(pieceIndex) || pieceIndex < 0) return null;
                const x = Number(b?.x);
                const y = Number(b?.y);
                if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
                return {
                    id:
                        typeof b?.id === "string" && b.id
                            ? b.id
                            : `main-sync-${pieceIndex}-${stamp}-${i}`,
                    pieceIndex,
                    x,
                    y,
                    ...(typeof b?.groupId === "string" && b.groupId
                        ? { groupId: b.groupId }
                        : {}),
                };
            })
            .filter(Boolean);
        const onBoard = new Set(nextBoard.map((b) => b.pieceIndex));
        setBoardPiecesScatter(nextBoard);
        setScatterPositions((prev) => {
            const next = { ...(prev && typeof prev === "object" ? prev : {}) };
            for (const idx of onBoard) {
                delete next[idx];
                delete next[String(idx)];
            }
            return next;
        });
    }, []);

    // Start session when template changes
    useEffect(() => {
        if (selectedTemplate?.id) {
            startPlaySession();
        }
    }, [selectedTemplate?.id, startPlaySession]);

    // Start play timer once session is active
    useEffect(() => {
        if (playSession && !playSession.isCompleted && playStartMsRef.current == null) {
            playStartMsRef.current = Date.now();
        }
    }, [playSession]);

    // Auto-save when game state changes (only triggered by actual piece movement)
    useEffect(() => {
        if (!playSessionRef.current || colorBasketMode) return;
        // Skip the first trigger which comes from loading initial state
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            return;
        }
        if (Object.keys(scatterPositions).length > 0 || boardPiecesScatter.length > 0) {
            scheduleAutoSave();
        }
    }, [scatterPositions, boardPiecesScatter, scheduleAutoSave, colorBasketMode]);

    // Check for completion — show success immediately; persist progress in the background.
    useEffect(() => {
        if (!isSolvedScatter || successShownRef.current) return;
        successShownRef.current = true;

        // If still paused at solve, exclude the open pause segment from wall-clock elapsed.
        if (timerPauseStartedAtRef.current != null && playStartMsRef.current != null) {
            playStartMsRef.current += Date.now() - timerPauseStartedAtRef.current;
            timerPauseStartedAtRef.current = null;
        }
        setTimerPaused(false);

        const formatSolveTime = (startedAt, finishedAt) => {
            const elapsedMs =
                finishedAt && startedAt
                    ? finishedAt - startedAt
                    : Date.now() - (startedAt ?? Date.now());
            const totalSec = Math.floor(Math.max(0, elapsedMs) / 1000);
            const m = Math.floor(totalSec / 60);
            const s = totalSec % 60;
            return `${m}:${String(s).padStart(2, "0")}`;
        };

        const computeAccuracy = (attemptsCount) => {
            const totalPieces = puzzleGrid > 0 ? puzzleGrid * puzzleGrid : 0;
            const attempts = Math.max(Number(attemptsCount) || moveCountRef.current || 1, 1);
            return totalPieces > 0
                ? Math.min(100, Math.round((totalPieces / attempts) * 100))
                : 100;
        };

        const localStartedAt = playStartMsRef.current ?? Date.now();
        setSuccessStats({
            time: formatSolveTime(localStartedAt, null),
            accuracy: computeAccuracy(moveCountRef.current),
        });
        setSuccessSpeedRank(null);
        triggerConfetti(10000);
        setSuccessRating(0);
        setShowSuccess(true);

        void (async () => {
            try {
                const result = playSessionRef.current
                    ? await saveProgressRef.current(true)
                    : null;
                if (result?.success && result.data) {
                    const { startedAt, finishedAt, attemptsCount, speedRank } = result.data;
                    setSuccessStats({
                        time: formatSolveTime(startedAt ?? localStartedAt, finishedAt),
                        accuracy: computeAccuracy(attemptsCount),
                    });
                    if (speedRank) {
                        setSuccessSpeedRank(speedRank);
                    }
                }
            } catch (error) {
                Logger.error("Error saving completion progress:", error);
            }
        })();
    }, [isSolvedScatter, puzzleGrid, triggerConfetti]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (autoSaveRef.current) {
                clearTimeout(autoSaveRef.current);
            }
        };
    }, []);

    const bringPieceToFront = useCallback((pieceIndex) => {
        pieceFocusCounterRef.current += 1;
        setPieceFocusOrder((prev) => ({
            ...prev,
            [pieceIndex]: pieceFocusCounterRef.current,
        }));
    }, []);

    const bringBoardGroupToFront = useCallback(
        (pieceIndex, groupId, laneBoardPieces) => {
            if (!groupId) {
                bringPieceToFront(pieceIndex);
                return;
            }
            for (const member of laneBoardPieces) {
                if (member.groupId === groupId) {
                    bringPieceToFront(member.pieceIndex);
                }
            }
        },
        [bringPieceToFront],
    );

    const handleMouseDown = useCallback((e, pieceIndex, opts) => {
        const {
            fromTray,
            fromScattered,
            listTray,
            boardLane,
            boardId,
            boardX,
            boardY,
            scatterX,
            scatterY,
            groupId,
        } = opts;
        e.preventDefault();
        if (!selectedTemplate || puzzleGrid <= 0) return;
        const lanePieces =
            boardLane === "main"
                ? boardPiecesMain
                : boardLane === "scatter"
                  ? boardPiecesScatter
                  : [];
        bringBoardGroupToFront(pieceIndex, groupId, lanePieces);
        const touch = e.touches ? e.touches[0] : e;
        const clientX = touch.clientX;
        const clientY = touch.clientY;
        const gridN = puzzleGrid;
        const cp = BOARD_SIZE / gridN;
        let grabDx = cp / 2;
        let grabDy = cp / 2;
        let ghostScale = boardScale;
        const elRect = e.currentTarget.getBoundingClientRect();
        if (fromTray && trayRef.current) {
            const rect = trayRef.current.getBoundingClientRect();
            const s = rect.width / BOARD_SIZE || 1;
            ghostScale = s;
            const pieceLeft = (pieceIndex % gridN) * cp;
            const pieceTop = Math.floor(pieceIndex / gridN) * cp;
            grabDx = (clientX - rect.left) / s - pieceLeft;
            grabDy = (clientY - rect.top) / s - pieceTop;
        } else if (fromScattered) {
            const boardRect = boardRefScatter.current?.getBoundingClientRect();
            const s = boardRect?.width ? boardRect.width / BOARD_SIZE : boardScale;
            ghostScale = s;
            if (boardRect) {
                if (listTray) {
                    const { x: pieceLeft, y: pieceTop } = listTrayPieceBoardTopLeft(
                        elRect.left,
                        elRect.top,
                        elRect.width,
                        boardRect,
                        cp,
                    );
                    const tabOffset = pieceVisualCellOffsetPx(cp);
                    grabDx = (clientX - boardRect.left) / s - pieceLeft - tabOffset;
                    grabDy = (clientY - boardRect.top) / s - pieceTop - tabOffset;
                } else if (scatterX != null && scatterY != null) {
                    // Scattered tray: scatterX/Y are already in board coordinates.
                    grabDx = (clientX - boardRect.left) / s - scatterX;
                    grabDy = (clientY - boardRect.top) / s - scatterY;
                } else {
                    grabDx = cp / 2;
                    grabDy = cp / 2;
                }
            } else {
                grabDx = cp / 2;
                grabDy = cp / 2;
            }
        } else if (boardLane === "main" && boardRefMain.current && boardX != null && boardY != null) {
            const rect = boardRefMain.current.getBoundingClientRect();
            const s = rect.width / BOARD_SIZE || 1;
            ghostScale = s;
            grabDx = (clientX - rect.left) / s - boardX;
            grabDy = (clientY - rect.top) / s - boardY;
        } else if (boardLane === "scatter" && boardRefScatter.current && boardX != null && boardY != null) {
            const rect = boardRefScatter.current.getBoundingClientRect();
            const s = rect.width / BOARD_SIZE || 1;
            ghostScale = s;
            grabDx = (clientX - rect.left) / s - boardX;
            grabDy = (clientY - rect.top) / s - boardY;
        }
        const dragStartBoardPositions = {};
        let groupDragMembers = null;
        if (boardId && boardLane) {
            const members = groupId
                ? lanePieces.filter((b) => b.groupId === groupId)
                : lanePieces.filter((b) => b.id === boardId);
            for (const member of members) {
                dragStartBoardPositions[member.id] = { x: member.x, y: member.y };
            }
            if (groupId && boardX != null && boardY != null && members.length > 1) {
                groupDragMembers = members.map((member) => ({
                    pieceIndex: member.pieceIndex,
                    dx: member.x - boardX,
                    dy: member.y - boardY,
                }));
            }
        }
        setDragging({
            pieceIndex,
            startX: clientX,
            startY: clientY,
            elLeft: elRect.left,
            elTop: elRect.top,
            elWidth: elRect.width,
            elHeight: elRect.height,
            fromTray: !!fromTray,
            fromScattered: !!fromScattered,
            listTray: !!listTray,
            boardLane: boardLane ?? null,
            boardId: boardId ?? null,
            grabDx,
            grabDy,
            ghostScale,
            groupId: groupId ?? null,
            groupDragMembers,
            anchorStartX: boardX ?? null,
            anchorStartY: boardY ?? null,
            dragStartBoardPositions,
        });
    }, [
        selectedTemplate,
        puzzleGrid,
        boardScale,
        bringBoardGroupToFront,
        boardPiecesMain,
        boardPiecesScatter,
    ]);

    useLayoutEffect(() => {
        if (!dragging || !dragGhostRef.current || puzzleGrid <= 0) return;
        const cellPx = BOARD_SIZE / puzzleGrid;
        const { left, top, scale } = getDragGhostBasePosition(dragging, cellPx);
        const ghost = dragGhostRef.current;
        ghost.style.left = `${left}px`;
        ghost.style.top = `${top}px`;
        ghost.style.transform = `scale(${scale})`;
    }, [dragging, puzzleGrid]);

    useEffect(() => {
        if (!dragging || !selectedTemplate || puzzleGrid <= 0) return;
        const gridN = puzzleGrid;
        const cellPxN = BOARD_SIZE / gridN;
        const tabMargin = cellPxN * 0.35;

        const inBoard = (clientX, clientY, ref) => {
            if (!ref?.current) return false;
            const r = ref.current.getBoundingClientRect();
            const x = clientX - r.left;
            const y = clientY - r.top;
            return x >= 0 && x < r.width && y >= 0 && y < r.height;
        };

        const inRect = (clientX, clientY, ref) => {
            if (!ref?.current) return false;
            const r = ref.current.getBoundingClientRect();
            return clientX >= r.left && clientX < r.right && clientY >= r.top && clientY < r.bottom;
        };

        const applySnap = (nx, ny, pIndex) =>
            applyHomeSnap(nx, ny, pIndex, gridN, cellPxN);

        const playAttachChime = () => {
            if (!soundEffectsEnabledRef.current) return;
            AudioService.play(AUDIO_EFFECT.JIGSAW_PIECE_SNAP);
        };

        const onMove = (e) => {
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            applyDragGhostTransform(dragGhostRef.current, dragging, clientX, clientY);
            if (e.cancelable) e.preventDefault();
        };
        const onUp = (e) => {
            const touch = e.changedTouches ? e.changedTouches[0] : e;
            const { pieceIndex, fromTray, fromScattered, boardLane, boardId, grabDx, grabDy } = dragging;
            const cx = touch.clientX;
            const cy = touch.clientY;

            const inMain = inBoard(cx, cy, boardRefMain);
            const inScatter = inBoard(cx, cy, boardRefScatter);

            const placeOnBoard = (ref, setBoard) => {
                if (!ref.current) return;
                const rect = ref.current.getBoundingClientRect();
                const s = rect.width / BOARD_SIZE || 1;
                let nx;
                let ny;
                if (dragging.listTray) {
                    const circleLeft = dragging.elLeft + (cx - dragging.startX);
                    const circleTop = dragging.elTop + (cy - dragging.startY);
                    const pos = listTrayDropCellOrigin(
                        circleLeft,
                        circleTop,
                        dragging.elWidth,
                        rect,
                        cellPxN,
                    );
                    nx = pos.x;
                    ny = pos.y;
                } else {
                    nx = (cx - rect.left) / s - grabDx;
                    ny = (cy - rect.top) / s - grabDy;
                }
                nx = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPxN + tabMargin, nx));
                ny = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPxN + tabMargin, ny));
                const snapped = applySnap(nx, ny, pieceIndex);
                setBoard((prev) => {
                    const newPiece = {
                        id: newBoardPieceId(),
                        pieceIndex,
                        x: snapped.x,
                        y: snapped.y,
                    };
                    const { pieces, attached } = placeBoardPieceWithAttach(
                        prev,
                        newPiece,
                        gridN,
                        cellPxN,
                    );
                    celebrateCorrectPlacement(prev, pieces, pieceIndex, gridN, cellPxN);
                    if (attached) playAttachChime();
                    return pieces;
                });
            };

            const moveOnBoard = (setBoard) => {
                if (!boardId) return;
                const ref = boardLane === "main" ? boardRefMain : boardRefScatter;
                if (!ref.current) return;
                const rect = ref.current.getBoundingClientRect();
                const s = rect.width / BOARD_SIZE || 1;
                let nx = (cx - rect.left) / s - grabDx;
                let ny = (cy - rect.top) / s - grabDy;
                nx = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPxN + tabMargin, nx));
                ny = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPxN + tabMargin, ny));
                setBoard((prev) => {
                    const { pieces } = moveBoardPieceGroup(
                        prev,
                        boardId,
                        nx,
                        ny,
                        gridN,
                        cellPxN,
                        { snapHome: false },
                    );
                    const { pieces: attachedPieces, attached } = tryAttachNeighbors(
                        pieces,
                        boardId,
                        gridN,
                        cellPxN,
                    );
                    const next = snapMovedPiecesToHome(attachedPieces, gridN, cellPxN);
                    celebrateCorrectPlacement(prev, next, pieceIndex, gridN, cellPxN);
                    if (attached) playAttachChime();
                    return next;
                });
            };

            if (inMain && boardRefMain.current) {
                if (fromTray) {
                    placeOnBoard(boardRefMain, setBoardPiecesMain);
                } else if (boardLane === "main" && boardId) {
                    moveOnBoard(setBoardPiecesMain);
                }
                setDragging(null);
                return;
            }

            if (inScatter && boardRefScatter.current) {
                if (fromScattered || fromTray) {
                    placeOnBoard(boardRefScatter, setBoardPiecesScatter);
                    moveCountRef.current += 1;
                } else if (boardLane === "scatter" && boardId) {
                    moveOnBoard(setBoardPiecesScatter);
                    moveCountRef.current += 1;
                }
                setDragging(null);
                return;
            }

            if (
                fromScattered &&
                !inScatter &&
                inRect(cx, cy, scatteredRef) &&
                scatteredRef.current
            ) {
                const rect = scatteredRef.current.getBoundingClientRect();
                const s = rect.width / BOARD_SIZE || 1;
                let nx = (cx - rect.left) / s - grabDx;
                let ny = (cy - rect.top) / s - grabDy;
                const maxX = Math.max(0, BOARD_SIZE - cellPxN);
                const maxY = Math.max(0, BOARD_SIZE - cellPxN);
                nx = Math.max(0, Math.min(maxX, nx));
                ny = Math.max(0, Math.min(maxY, ny));
                setScatterPositions((prev) => ({
                    ...prev,
                    [pieceIndex]: { x: nx, y: ny },
                }));
                setDragging(null);
                return;
            }

            if (!fromTray && !fromScattered && boardId) {
                if (boardLane === "main" && inRect(cx, cy, trayRef)) {
                    setBoardPiecesMain((prev) => prev.filter((b) => b.id !== boardId));
                }
                if (boardLane === "scatter" && inRect(cx, cy, scatteredRef)) {
                    setBoardPiecesScatter((prev) => prev.filter((b) => b.id !== boardId));
                }
            }

            setDragging(null);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        window.addEventListener("touchmove", onMove, { passive: false });
        window.addEventListener("touchend", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onUp);
        };
    }, [dragging, selectedTemplate, puzzleGrid, celebrateCorrectPlacement]);

    const handleResetMain = useCallback(() => setBoardPiecesMain([]), []);
    const handleResetScatter = useCallback(() => {
        setBoardPiecesScatter([]);
        setDragging(null);
        setSettingsAnchor(null);
    }, []);

    /**
     * Admin/dev: place every piece at home except one left in the tray
     * so the board is one move from solved.
     */
    const handleAdminPlaceAllButOne = useCallback(() => {
        if (colorBasketMode) return;
        if (!selectedTemplate || puzzleGrid <= 0) return;
        const gridN = puzzleGrid;
        const cellPxN = BOARD_SIZE / gridN;
        const totalN = gridN * gridN;
        if (totalN < 2) return;

        const leaveIndex = totalN - 1;
        const stamp = Date.now().toString(36);
        const pieces = [];
        for (let i = 0; i < totalN; i += 1) {
            if (i === leaveIndex) continue;
            pieces.push({
                id: `bp-admin-${stamp}-${i}`,
                pieceIndex: i,
                x: (i % gridN) * cellPxN,
                y: Math.floor(i / gridN) * cellPxN,
            });
        }
        const attached =
            pieces.length > 0
                ? tryAttachNeighbors(pieces, pieces[0].id, gridN, cellPxN).pieces
                : pieces;
        setBoardPiecesScatter(attached);
        setScatterPositions((prev) => {
            const next = { ...(prev && typeof prev === "object" ? prev : {}) };
            for (let i = 0; i < totalN; i += 1) {
                if (i === leaveIndex) continue;
                delete next[i];
                delete next[String(i)];
            }
            return next;
        });
        setDragging(null);
        setSettingsAnchor(null);
    }, [colorBasketMode, puzzleGrid, selectedTemplate]);

    const isBoardPieceAtHome = useCallback((b, gridN, cellPxN) => {
        const homeX = (b.pieceIndex % gridN) * cellPxN;
        const homeY = Math.floor(b.pieceIndex / gridN) * cellPxN;
        const tol = cellPxN * 0.22;
        return Math.abs(b.x - homeX) <= tol && Math.abs(b.y - homeY) <= tol;
    }, []);

    /** Keep only pieces snapped to their home cell on the play board; others return to the tray. */
    const handleSweepMisplacedFromBoard = useCallback(() => {
        if (!selectedTemplate) return;
        const gridN = puzzleGrid;
        const cellPxN = BOARD_SIZE / gridN;
        const templateKey =
            selectedTemplate.id ?? selectedTemplate.jigsawTemplateId ?? String(gridN);
        let removedPieceIndices = [];

        setBoardPiecesScatter((prev) => {
            const kept = [];
            removedPieceIndices = [];
            for (const b of prev) {
                if (isBoardPieceAtHome(b, gridN, cellPxN)) {
                    kept.push(b);
                } else {
                    removedPieceIndices.push(b.pieceIndex);
                }
            }
            return kept;
        });

        if (removedPieceIndices.length === 0) return;

        const tot = gridN * gridN;
        const layoutKey = `${templateKey}-sweep-${Date.now()}`;
        const freshTrayLayout = buildScatterPositions(tot, gridN, layoutKey);
        setScatterPositions((posPrev) => {
            const next = { ...posPrev };
            for (const pieceIndex of removedPieceIndices) {
                next[pieceIndex] = freshTrayLayout[pieceIndex];
            }
            return next;
        });
        scheduleAutoSave();
    }, [selectedTemplate, puzzleGrid, scheduleAutoSave, isBoardPieceAtHome]);

    const hasMisplacedOnBoard = useMemo(() => {
        if (!selectedTemplate || puzzleGrid <= 0 || boardPiecesScatter.length === 0) return false;
        const gridN = puzzleGrid;
        const cellPxN = BOARD_SIZE / gridN;
        return boardPiecesScatter.some((b) => !isBoardPieceAtHome(b, gridN, cellPxN));
    }, [selectedTemplate, puzzleGrid, boardPiecesScatter, isBoardPieceAtHome]);

    const story = group?.story ?? null;
    const imageUrl = getImageUrl(story);
    const title = getStoryTitle(story) || group?.puzzleTemplate?.title || "";
    const templates = group?.templates ?? [];

    /** Social share always uses 12×12 when available (Facebook/Instagram preview + `/share/JIGSAW/{id}`). */
    const jigsawShareTemplate = useMemo(
        () => resolveJigsawShareTemplate(templates) ?? selectedTemplate,
        [templates, selectedTemplate],
    );
    const jigsawShareId = (() => {
        const raw = jigsawShareTemplate?.id;
        const s = raw != null ? String(raw).trim() : "";
        return s || null;
    })();
    const jigsawShareUrl = jigsawShareId ? buildShareUrl(jigsawShareId, "JIGSAW") : "";
    const templatePreviewImageUrl = (() => {
        const u =
            jigsawShareTemplate?.previewImageUrl ??
            jigsawShareTemplate?.preview_image_url;
        return typeof u === "string" && u.trim() ? u.trim() : "";
    })();
    const canShareJigsaw = Boolean(jigsawShareUrl && templatePreviewImageUrl);

    const playBoardSeoNoindex =
        !loading && Boolean(jigsawId) && (!group || !selectedTemplate);

    useEffect(() => {
        AudioService.preload(AUDIO_EFFECT.JIGSAW_PIECE_SNAP);
        AudioService.preload(AUDIO_EFFECT.SLIDING_LINE_MATCH);
        return () => {
            AudioService.release(AUDIO_EFFECT.JIGSAW_PIECE_SNAP);
            AudioService.release(AUDIO_EFFECT.SLIDING_LINE_MATCH);
        };
    }, []);

    const accessContext = useMemo(() => buildPuzzleAccessContext(userInfo), [userInfo]);
    const isFreePuzzle = group?.storyJigsaw?.free === true;
    const boardLockReason = useMemo(() => {
        if (!selectedTemplate) return null;
        return getJigsawTileLockReason(
            resolveJigsawTemplateTileSize(selectedTemplate),
            accessContext,
            { isFreePuzzle },
        );
    }, [selectedTemplate, accessContext, isFreePuzzle]);

    const needsPremiumCreditCharge = useMemo(() => {
        if (!jigsawId || !selectedTemplate || boardLockReason) return false;
        if (accessContext.isStaff || isFreePuzzle) return false;
        return isJigsawPremiumTier(resolveJigsawTemplateTileSize(selectedTemplate));
    }, [jigsawId, selectedTemplate, boardLockReason, accessContext.isStaff, isFreePuzzle]);

    useEffect(() => {
        let cancelled = false;

        async function chargePremiumCredit() {
            if (!needsPremiumCreditCharge || !jigsawId) {
                setPremiumCreditStatus(needsPremiumCreditCharge ? null : "ok");
                setPremiumCreditError("");
                return;
            }
            setPremiumCreditStatus("pending");
            setPremiumCreditError("");
            try {
                const result = await HomeCreditService.consumePremiumPlay(jigsawId);
                if (cancelled) return;
                setPremiumCreditStatus("ok");
                if (result.creditsConsumed > 0) {
                    trackEvent("jigsaw_premium_credit_consumed", {
                        puzzle_id: jigsawId,
                        credits_consumed: result.creditsConsumed,
                        credit_balance: result.creditBalance,
                    });
                }
            } catch (err) {
                if (cancelled) return;
                const message = err?.message || "";
                Logger.warn("Jigsaw premium credit consume failed:", message);
                if (/insufficient/i.test(message)) {
                    setPremiumCreditStatus("insufficient");
                    setPremiumCreditError(message);
                    setCreditAlertInsufficient(true);
                    setCreditAlertMessage(
                        message.trim() ||
                            t("core.puzzleAccess.insufficientCredits", "Insufficient credits"),
                    );
                    setCreditAlertOpen(true);
                } else {
                    setPremiumCreditStatus("error");
                    setPremiumCreditError(message);
                    setCreditAlertInsufficient(false);
                    setCreditAlertMessage(
                        message.trim() ||
                            t("core.puzzleAccess.creditChargeError", "Could not spend credits."),
                    );
                    setCreditAlertOpen(true);
                }
            }
        }

        chargePremiumCredit();
        return () => {
            cancelled = true;
        };
    }, [needsPremiumCreditCharge, jigsawId, t]);

    const isAuthenticated = Boolean(userInfo?.cloudUser);
    const [progressGift, setProgressGift] = useState(null);
    const progressGiftQueueRef = useRef([]);
    const giftPaidRef = useRef(new Set());

    const showNextProgressGift = useCallback(() => {
        const next = progressGiftQueueRef.current.shift() || null;
        setProgressGift(next);
    }, []);

    const enqueueProgressGift = useCallback((gift) => {
        setProgressGift((current) => {
            if (current) {
                progressGiftQueueRef.current.push(gift);
                return current;
            }
            return gift;
        });
    }, []);

    useEffect(() => {
        giftPaidRef.current = new Set();
        progressGiftQueueRef.current = [];
        setProgressGift(null);
    }, [selectedTemplate?.id]);

    const atHomeCount = useMemo(() => {
        if (puzzleGrid <= 0) return 0;
        const cell = BOARD_SIZE / puzzleGrid;
        return boardPiecesScatter.reduce(
            (count, piece) => count + (isBoardPieceAtHome(piece, puzzleGrid, cell) ? 1 : 0),
            0,
        );
    }, [boardPiecesScatter, puzzleGrid, isBoardPieceAtHome]);

    useEffect(() => {
        if (!isAuthenticated || colorBasketMode) return;
        const puzzleId = selectedTemplate?.id;
        const gridN = puzzleGrid;
        if (!puzzleId || gridN < 12) return;
        const totalN = gridN * gridN;
        if (totalN <= 0 || atHomeCount <= 0) return;
        const ratio = atHomeCount / totalN;
        const kinds = [];
        if (ratio >= 0.25) kinds.push("25");
        if (ratio >= 0.5) kinds.push("50");
        if (ratio >= 0.75) kinds.push("75");
        kinds.forEach((kind) => {
            const key = `${puzzleId}:${kind}`;
            if (giftPaidRef.current.has(key)) return;
            giftPaidRef.current.add(key);
            HomeCreditService.grantJigsawMilestone({ puzzleId, grid: gridN, kind })
                .then((result) => {
                    const awarded = Number(result?.awarded) || 0;
                    if (awarded > 0) {
                        enqueueProgressGift({
                            awarded,
                            label: `${kind}%`,
                            balance: Number(result?.creditBalance) || 0,
                        });
                    }
                })
                .catch((err) => {
                    giftPaidRef.current.delete(key);
                    Logger.warn("Jigsaw milestone:", err?.message || err);
                });
        });
    }, [
        atHomeCount,
        puzzleGrid,
        selectedTemplate?.id,
        isAuthenticated,
        colorBasketMode,
        enqueueProgressGift,
    ]);
    const isAdminRole = userInfo?.cloudUser?.userRole?.toUpperCase() === "ADMIN";
    const userPayPlan = UtilService.resolveWebPayPlan(userInfo?.cloudUser);

    const grid = puzzleGrid;
    const cellPx = grid > 0 ? BOARD_SIZE / grid : 0;
    const total = grid * grid;
    const placedCountMain = boardPiecesMain.length;
    const placedCountScatter = boardPiecesScatter.length;
    const tol = cellPx * 0.22;
    const solvedFor = (pieces) =>
        total > 0 &&
        pieces.length === total &&
        pieces.every((b) => {
            const hx = (b.pieceIndex % grid) * cellPx;
            const hy = Math.floor(b.pieceIndex / grid) * cellPx;
            return Math.abs(b.x - hx) < tol && Math.abs(b.y - hy) < tol;
        });
    const isSolvedMain = solvedFor(boardPiecesMain);
    const piecesScatteredTray = Array.from({ length: total }, (_, i) => i).filter(
        (p) => !boardPiecesScatter.some((b) => b.pieceIndex === p),
    );

    // Stable tray order for this puzzle — shuffle once by grid/template, never re-order when pieces leave.
    // After shuffle, edge pieces are moved to the front (edges stay shuffled among themselves).
    const stableTrayOrder = useMemo(() => {
        if (total <= 0) return [];
        const arr = Array.from({ length: total }, (_, i) => i);
        const templateKey = String(
            selectedTemplate?.id ?? selectedTemplate?.jigsawTemplateId ?? "",
        );
        return shuffleTrayPieceIndices(arr, grid, templateKey);
    }, [total, grid, selectedTemplate?.id, selectedTemplate?.jigsawTemplateId]);

    const shuffledScatteredTray = useMemo(() => {
        const onBoard = new Set(boardPiecesScatter.map((b) => b.pieceIndex));
        return stableTrayOrder.filter((pieceIndex) => !onBoard.has(pieceIndex));
    }, [stableTrayOrder, boardPiecesScatter]);

    /** Tray: all 3 at pick/all; current only during one-by-one. */
    const placementHintTrayIndices = useMemo(() => {
        if (!placementHint?.pieceIndices?.length) return [];
        if (placementHint.mode === "step") {
            const step = placementHint.stepIndex;
            if (step == null || step < 0 || step >= placementHint.pieceIndices.length) return [];
            return [placementHint.pieceIndices[step]];
        }
        return placementHint.pieceIndices;
    }, [placementHint]);

    /** Board: none at pick; current during step; all during final hold. */
    const placementHintBoardIndices = useMemo(() => {
        if (!placementHint?.pieceIndices?.length) return [];
        if (placementHint.mode === "pick") return [];
        if (placementHint.mode === "all") return placementHint.pieceIndices;
        const step = placementHint.stepIndex;
        if (step == null || step < 0 || step >= placementHint.pieceIndices.length) return [];
        return [placementHint.pieceIndices[step]];
    }, [placementHint]);

    const placementHintTraySet = useMemo(
        () => new Set(placementHintTrayIndices),
        [placementHintTrayIndices],
    );

    const trayFrontActive = Boolean(placementHint);
    const trayAreaZIndex = trayFrontActive ? Z_TRAY_HINT_FRONT : Z_TRAY_AREA;

    useEffect(() => {
        if (!placementHintTrayIndices.length || pieceViewMode !== "list") return;
        const focusIndex = placementHintTrayIndices[0];
        if (focusIndex == null) return;
        const el = scatteredRef.current?.querySelector?.(
            `[data-jigsaw-tray-piece="${focusIndex}"]`,
        );
        el?.scrollIntoView?.({ behavior: "smooth", inline: "center", block: "nearest" });
    }, [placementHintTrayIndices, pieceViewMode]);

    const paths = selectedTemplate?.piecePaths?.pieces;
    const svgPathFallback = selectedTemplate?.svgPath;

    const isMobilePortrait = useMediaQuery("(max-width: 899.95px) and (orientation: portrait)");

    /** Promo portrait uses phone-style stack (board + under-board tray), not desktop side tray. */
    const mobilePortraitCompact = isCaptureLandscape
        ? false
        : isMobilePortrait || isPromoPortraitCapture;
    const isDesktopPlayLayout = !mobilePortraitCompact;

    useEffect(() => {
        if (isCaptureLandscape || isPromoPortraitCapture) return undefined;
        const el = puzzleColumnRef.current;
        if (!el) return undefined;
        const onWheel = (event) => {
            const pinchZoom = event.ctrlKey || event.metaKey;
            if (!pinchZoom && boardZoom > 1) return;
            if (!event.deltaY) return;
            event.preventDefault();
            const now = Date.now();
            if (now < wheelZoomLockRef.current) return;
            wheelZoomAccumRef.current += event.deltaY;
            const threshold = event.deltaMode === 1 ? 1 : 50;
            if (Math.abs(wheelZoomAccumRef.current) < threshold) return;
            const direction = wheelZoomAccumRef.current < 0 ? 1 : -1;
            wheelZoomAccumRef.current = 0;
            wheelZoomLockRef.current = now + 120;
            handleBoardZoomStep(direction);
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, [
        handleBoardZoomStep,
        isCaptureLandscape,
        isPromoPortraitCapture,
        loading,
        boardLockReason,
        selectedTemplate?.id,
        mobilePortraitCompact,
        isSolvedScatter,
        boardZoom,
    ]);

    useEffect(() => {
        if (isCaptureLandscape || isPromoPortraitCapture || boardZoom <= 1) return undefined;
        const el = puzzleColumnRef.current;
        if (!el) return undefined;
        let activePointer = null;
        let originX = 0;
        let originY = 0;
        let originLeft = 0;
        let originTop = 0;
        let panRaf = 0;
        let nextLeft = 0;
        let nextTop = 0;

        const finish = (event) => {
            if (activePointer == null || event.pointerId !== activePointer) return;
            activePointer = null;
            if (panRaf) {
                cancelAnimationFrame(panRaf);
                panRaf = 0;
                el.scrollLeft = nextLeft;
                el.scrollTop = nextTop;
            }
            el.style.cursor = "grab";
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", finish);
            window.removeEventListener("pointercancel", finish);
        };
        const onPointerMove = (event) => {
            if (activePointer == null || event.pointerId !== activePointer) return;
            nextLeft = originLeft - (event.clientX - originX);
            nextTop = originTop - (event.clientY - originY);
            event.preventDefault();
            if (panRaf) return;
            panRaf = requestAnimationFrame(() => {
                panRaf = 0;
                el.scrollLeft = nextLeft;
                el.scrollTop = nextTop;
            });
        };
        const onPointerDown = (event) => {
            // Touch scrolling is native on the overflow frame. A second JS drag fights it.
            if (event.pointerType === "touch") return;
            if (event.button != null && event.button !== 0) return;
            event.preventDefault();
            event.stopPropagation();
            activePointer = event.pointerId;
            originX = event.clientX;
            originY = event.clientY;
            originLeft = el.scrollLeft;
            originTop = el.scrollTop;
            nextLeft = originLeft;
            nextTop = originTop;
            el.style.cursor = "grabbing";
            window.addEventListener("pointermove", onPointerMove);
            window.addEventListener("pointerup", finish);
            window.addEventListener("pointercancel", finish);
        };

        el.addEventListener("pointerdown", onPointerDown, true);
        return () => {
            if (panRaf) cancelAnimationFrame(panRaf);
            el.style.cursor = "";
            el.removeEventListener("pointerdown", onPointerDown, true);
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", finish);
            window.removeEventListener("pointercancel", finish);
        };
    }, [
        boardZoom,
        isCaptureLandscape,
        isPromoPortraitCapture,
        loading,
        boardLockReason,
        selectedTemplate?.id,
        mobilePortraitCompact,
        trayBesideBoard,
        isSolvedScatter,
        playBoardSquarePx,
    ]);
    const isScatteredView = pieceViewMode === "scattered";

    useLayoutEffect(() => {
        if (mobilePortraitCompact || isCaptureLandscape) return undefined;
        const syncDocViewport = () => {
            const w = document.documentElement.clientWidth;
            const h = document.documentElement.clientHeight;
            if (w <= 0 || h <= 0) return;
            setDocViewportPx((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
        };
        syncDocViewport();
        window.addEventListener("resize", syncDocViewport);
        window.visualViewport?.addEventListener?.("resize", syncDocViewport);
        return () => {
            window.removeEventListener("resize", syncDocViewport);
            window.visualViewport?.removeEventListener?.("resize", syncDocViewport);
        };
    }, [mobilePortraitCompact, isCaptureLandscape]);

    useEffect(() => {
        if (!isCaptureLandscape) return undefined;
        const html = document.documentElement;
        const body = document.body;
        const root = document.getElementById("root");
        const prev = {
            htmlOverflow: html.style.overflow,
            bodyOverflow: body.style.overflow,
            htmlW: html.style.width,
            htmlH: html.style.height,
            bodyW: body.style.width,
            bodyH: body.style.height,
            bodyMargin: body.style.margin,
            rootW: root?.style.width,
            rootH: root?.style.height,
            rootOverflow: root?.style.overflow,
        };
        html.style.overflow = "hidden";
        body.style.overflow = "hidden";
        html.style.width = `${CAPTURE_FRAME_W}px`;
        html.style.height = `${CAPTURE_FRAME_H}px`;
        body.style.width = `${CAPTURE_FRAME_W}px`;
        body.style.height = `${CAPTURE_FRAME_H}px`;
        body.style.margin = "0";
        if (root) {
            root.style.width = `${CAPTURE_FRAME_W}px`;
            root.style.height = `${CAPTURE_FRAME_H}px`;
            root.style.overflow = "hidden";
        }
        setDocViewportPx({ w: CAPTURE_FRAME_W, h: CAPTURE_FRAME_H });
        return () => {
            html.style.overflow = prev.htmlOverflow;
            body.style.overflow = prev.bodyOverflow;
            html.style.width = prev.htmlW;
            html.style.height = prev.htmlH;
            body.style.width = prev.bodyW;
            body.style.height = prev.bodyH;
            body.style.margin = prev.bodyMargin;
            if (root) {
                root.style.width = prev.rootW || "";
                root.style.height = prev.rootH || "";
                root.style.overflow = prev.rootOverflow || "";
            }
        };
    }, [isCaptureLandscape]);

    /** Single scale for tray pieces — must match painted board (same source as drag ghost). */
    const trayPieceScale = useMemo(() => {
        // Divide out board zoom: tray pieces stay at fit scale, else the tray strip
        // grows with zoom and shrinks the fitted board on the next measure pass.
        if (boardSquarePx != null && boardSquarePx > 0) {
            return boardSquarePx / boardZoom / BOARD_SIZE;
        }
        if (playBoardSquarePx > 0) return playBoardSquarePx / BOARD_SIZE;
        return boardScale;
    }, [boardSquarePx, playBoardSquarePx, boardScale, boardZoom]);

    /** Tray strip/circle chrome at scale 1 — used only for viewport budgeting (avoids resize loops). */
    const listTrayChrome = useMemo(() => {
        if (grid <= 0) {
            return { circleOuterPx: 40, pieceScreenPx: 35, stripHeightPx: 64, trayScale: LIST_TRAY_PIECE_DISPLAY_SCALE };
        }
        return listTrayMetrics(BOARD_SIZE / grid, 1);
    }, [grid]);

    const stableTrayRowPx = listTrayChrome.stripHeightPx;

    /** Scaled tray chrome — matches on-screen piece size (display only). */
    const listTrayDisplay = useMemo(() => {
        if (grid <= 0) {
            return { circleOuterPx: 40, pieceScreenPx: 35, stripHeightPx: 64, trayScale: LIST_TRAY_PIECE_DISPLAY_SCALE };
        }
        if (isCaptureLandscape) {
            const boardSide =
                playBoardSquarePx > 0
                    ? playBoardSquarePx
                    : computeCapture1080BoardSidePx(grid);
            return listTrayMetrics(BOARD_SIZE / grid, captureTrayBoardScaleArg(boardSide));
        }
        const scale = isPromoPortraitCapture
            ? trayPieceScale * PROMO_PORTRAIT_TRAY_SCALE_BOOST
            : trayPieceScale;
        const metrics = listTrayMetrics(BOARD_SIZE / grid, scale);
        if (isPromoPortraitCapture) {
            const rows = PROMO_PORTRAIT_TRAY_ROWS;
            const gap = 8;
            return {
                ...metrics,
                stripHeightPx:
                    metrics.circleOuterPx * rows +
                    gap * Math.max(0, rows - 1) +
                    LIST_TRAY_STRIP_SLACK_PX +
                    LIST_TRAY_SCROLLBAR_RESERVE_PX,
            };
        }
        return metrics;
    }, [grid, trayPieceScale, isCaptureLandscape, isPromoPortraitCapture, playBoardSquarePx]);

    /** Capture: lock board side from 1920×1080 math only. Desktop: measure root. */
    useLayoutEffect(() => {
        if (mobilePortraitCompact) return undefined;

        if (isCaptureLandscape) {
            const boardSide = computeCapture1080BoardSidePx(grid);
            if (boardSide >= 20) {
                setPlayBoardSquarePx((prev) => (prev === boardSide ? prev : boardSide));
            }
            return undefined;
        }

        const rootEl = playRootRef.current;
        if (!rootEl) return undefined;

        let rafId = 0;
        const measureSquares = () => {
                const rootRect = rootEl.getBoundingClientRect();
                const rootH = rootRect.height > 0 ? rootRect.height : docViewportPx.h;
                const rootW = rootRect.width > 0 ? rootRect.width : docViewportPx.w;
                if (rootH <= 0) return;

                const trayPxStable = stableTrayRowPx + 8;
                const heightCap = Math.max(0, rootH - DESKTOP_PLAY_CHROME_PX);
                let vhCap = Math.max(0, heightCap - trayPxStable);

                const availW = Math.max(0, rootW - DESKTOP_PLAY_PAD_PX);
                let boardSide = 0;
                const sideGap = 16;

                boardSide = Math.floor(Math.min(availW, vhCap));

                if (boardSide >= 20 && grid > 0) {
                    const scaledTray =
                        listTrayMetrics(BOARD_SIZE / grid, boardSide / BOARD_SIZE).stripHeightPx + 8;
                    const vhCap2 = Math.max(0, heightCap - scaledTray);
                    if (vhCap2 < vhCap) {
                        boardSide = Math.floor(Math.min(availW, vhCap2));
                    }
                }

                let beside = false;
                if (pieceViewMode === "list" && grid > 0 && boardSide >= 160) {
                    const metrics = listTrayMetrics(BOARD_SIZE / grid, boardSide / BOARD_SIZE);
                    const trayCol = Math.ceil((metrics.circleOuterPx || 72) + 12);
                    if (availW - boardSide >= trayCol + 48) {
                        const provisional = Math.floor(
                            Math.min(Math.max(0, availW - trayCol - sideGap), heightCap),
                        );
                        const fittedMetrics = listTrayMetrics(
                            BOARD_SIZE / grid,
                            Math.max(provisional, 20) / BOARD_SIZE,
                        );
                        const fittedTray = Math.ceil((fittedMetrics.circleOuterPx || 72) + 12);
                        const fitted = Math.floor(
                            Math.min(Math.max(0, availW - fittedTray - sideGap), heightCap),
                        );
                        if (fitted >= 160 && availW - fitted >= fittedTray) {
                            beside = true;
                            boardSide = fitted;
                        }
                    }
                }
                setTrayBesideBoard((prev) => (prev === beside ? prev : beside));
                if (boardSide >= 20) {
                    setPlayBoardSquarePx((prev) => (prev === boardSide ? prev : boardSide));
                }
                const surfaceEl = playSurfaceRef.current;
                if (surfaceEl) {
                    const nextBox = {
                        w: surfaceEl.clientWidth,
                        h: surfaceEl.clientHeight,
                    };
                    setPlaySurfaceBox((prev) =>
                        prev.w === nextBox.w && prev.h === nextBox.h ? prev : nextBox,
                    );
                }
        };
        const syncSquares = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                measureSquares();
            });
        };

        measureSquares();
        const observer = new ResizeObserver(syncSquares);
        observer.observe(rootEl);
        if (playSurfaceRef.current) observer.observe(playSurfaceRef.current);
        window.addEventListener("resize", syncSquares);
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            observer.disconnect();
            window.removeEventListener("resize", syncSquares);
        };
    }, [
        mobilePortraitCompact,
        isCaptureLandscape,
        referenceOnScreen,
        loading,
        grid,
        stableTrayRowPx,
        docViewportPx.h,
        docViewportPx.w,
        pieceViewMode,
    ]);

    const attachPuzzleColumnRef = useCallback((node) => {
        puzzleColumnRef.current = node;
    }, []);

    const mobilePortraitBoardCapSx = mobilePortraitCompact
        ? { width: "100%", minWidth: 0, maxWidth: "100%" }
        : { width: "100%", minWidth: 0 };

    const toggleReferenceOnScreen = useCallback(() => {
        setReferenceOnScreen((prev) => {
            const next = !prev;
            persistReferenceOnScreen(next);
            return next;
        });
    }, []);

    const hideReferenceOnScreen = useCallback(() => {
        setReferenceOnScreen(false);
        persistReferenceOnScreen(false);
    }, []);

    const stepReferenceImageZoom = useCallback((direction) => {
        setReferenceImageZoom((prev) => {
            const next = Math.round((prev + direction * REFERENCE_IMAGE_ZOOM_STEP) * 100) / 100;
            return Math.min(REFERENCE_IMAGE_ZOOM_MAX, Math.max(REFERENCE_IMAGE_ZOOM_MIN, next));
        });
    }, []);

    const paintReferenceTransform = useCallback(() => {
        const node = referenceImageRef.current;
        if (!node) return;
        const { x, y } = referencePanRef.current;
        node.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${referenceZoomRef.current})`;
        node.style.transformOrigin = "center top";
    }, []);

    useLayoutEffect(() => {
        paintReferenceTransform();
    }, [paintReferenceTransform, referenceImageZoom, referenceOnScreen]);

    /** Two-finger trackpad scroll and two-finger touch slide the preview inside its frame. */
    useEffect(() => {
        const el = referenceClipRef.current;
        if (!el || !referenceOnScreen) return undefined;

        const clampPan = (x, y, zoom) => {
            const clipW = el.clientWidth;
            const clipH = el.clientHeight;
            if (clipW <= 0 || clipH <= 0) return { x: 0, y: 0 };
            const visual = zoom * clipW;
            const overflowX = Math.max(0, (visual - clipW) / 2);
            const slackY = clipH - visual;
            return {
                x: Math.min(overflowX, Math.max(-overflowX, x)),
                y: Math.min(Math.max(0, slackY), Math.max(Math.min(0, slackY), y)),
            };
        };

        let paintRaf = 0;
        const schedulePaint = () => {
            if (paintRaf) return;
            paintRaf = requestAnimationFrame(() => {
                paintRaf = 0;
                paintReferenceTransform();
            });
        };

        const applyPan = (x, y) => {
            const next = clampPan(x, y, referenceZoomRef.current);
            const prev = referencePanRef.current;
            if (prev.x === next.x && prev.y === next.y) return;
            referencePanRef.current = next;
            schedulePaint();
        };

        const clampedNow = clampPan(
            referencePanRef.current.x,
            referencePanRef.current.y,
            referenceZoomRef.current,
        );
        if (
            clampedNow.x !== referencePanRef.current.x
            || clampedNow.y !== referencePanRef.current.y
        ) {
            referencePanRef.current = clampedNow;
            paintReferenceTransform();
        }

        const onWheel = (event) => {
            if (event.ctrlKey || event.metaKey) return;
            event.preventDefault();
            const cur = referencePanRef.current;
            applyPan(cur.x - event.deltaX, cur.y - event.deltaY);
        };

        let gesture = null;
        const onTouchStart = (event) => {
            if (event.touches.length !== 2) {
                gesture = null;
                return;
            }
            event.preventDefault();
            const a = event.touches[0];
            const b = event.touches[1];
            gesture = {
                x: (a.clientX + b.clientX) / 2,
                y: (a.clientY + b.clientY) / 2,
                panX: referencePanRef.current.x,
                panY: referencePanRef.current.y,
            };
        };
        const onTouchMove = (event) => {
            if (!gesture || event.touches.length !== 2) return;
            event.preventDefault();
            const a = event.touches[0];
            const b = event.touches[1];
            const x = (a.clientX + b.clientX) / 2;
            const y = (a.clientY + b.clientY) / 2;
            applyPan(gesture.panX + (x - gesture.x), gesture.panY + (y - gesture.y));
        };
        const onTouchEnd = (event) => {
            if (event.touches.length < 2) gesture = null;
        };

        el.addEventListener("wheel", onWheel, { passive: false });
        el.addEventListener("touchstart", onTouchStart, { passive: false });
        el.addEventListener("touchmove", onTouchMove, { passive: false });
        el.addEventListener("touchend", onTouchEnd);
        el.addEventListener("touchcancel", onTouchEnd);
        return () => {
            if (paintRaf) cancelAnimationFrame(paintRaf);
            el.removeEventListener("wheel", onWheel);
            el.removeEventListener("touchstart", onTouchStart);
            el.removeEventListener("touchmove", onTouchMove);
            el.removeEventListener("touchend", onTouchEnd);
            el.removeEventListener("touchcancel", onTouchEnd);
        };
    }, [
        referenceOnScreen,
        referenceImageZoom,
        playBoardSquarePx,
        playSurfaceBox.w,
        playSurfaceBox.h,
        mobilePortraitCompact,
        isSolvedScatter,
        paintReferenceTransform,
    ]);

    const handleToggleTimerPause = useCallback(() => {
        setTimerPaused((prev) => {
            const next = !prev;
            if (next) {
                timerPauseStartedAtRef.current = Date.now();
            } else if (timerPauseStartedAtRef.current != null && playStartMsRef.current != null) {
                // Shift start forward so success time excludes paused duration.
                playStartMsRef.current += Date.now() - timerPauseStartedAtRef.current;
                timerPauseStartedAtRef.current = null;
            } else {
                timerPauseStartedAtRef.current = null;
            }
            return next;
        });
    }, []);

    useEffect(() => {
        if (!selectedTemplate || puzzleGrid <= 0 || isSolvedScatter) return undefined;
        if (visibilityPaused) {
            if (!timerPaused && timerPauseStartedAtRef.current == null) {
                timerPauseStartedAtRef.current = Date.now();
            }
            return undefined;
        }
        if (!timerPaused && timerPauseStartedAtRef.current != null && playStartMsRef.current != null) {
            playStartMsRef.current += Date.now() - timerPauseStartedAtRef.current;
            timerPauseStartedAtRef.current = null;
        }
        return undefined;
    }, [visibilityPaused, timerPaused, selectedTemplate, puzzleGrid, isSolvedScatter]);

    const handleToggleSoundEffects = useCallback(() => {
        setSoundEffectsEnabled((prev) => {
            const next = !prev;
            void (async () => {
                try {
                    const info = await UserService.getUserInfo();
                    const id = cloudUserId || info?.cloudUser?.id;
                    if (!id) return;
                    await UserService.updateUserProfile(id, {
                        ...UserService.getSettingsFormData(info),
                        soundEnabled: next,
                    });
                } catch (e) {
                    Logger.warn("JigsawPlayBoardPage save soundEnabled:", e);
                }
            })();
            return next;
        });
    }, [cloudUserId]);

    const loadWalletBalances = useCallback(async () => {
        if (!isAuthenticated) {
            setWalletPlayCredits(null);
            setWalletHintCredits(null);
            setWalletPuzzleCoins(null);
            return;
        }
        setWalletBalancesLoading(true);
        try {
            const [play, hints, freshUserInfo] = await Promise.all([
                HomeCreditService.fetchBalance(HomeCreditService.WEB_APP_CODE).catch(() => null),
                HomeCreditService.fetchBalance(HomeCreditService.JIGSAW_MUSE_HINTS_CODE).catch(() => null),
                UserService.getUserInfo().catch(() => null),
            ]);
            setWalletPlayCredits(play?.creditBalance ?? 0);
            setWalletHintCredits(hints?.creditBalance ?? 0);
            setWalletPuzzleCoins(resolveWebHintPuzzleCoins(freshUserInfo?.cloudUser));
        } catch (err) {
            Logger.warn("JigsawPlayBoardPage load wallet balances:", err);
            setWalletPlayCredits(0);
            setWalletHintCredits(0);
            setWalletPuzzleCoins(0);
        } finally {
            setWalletBalancesLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!settingsAnchor || !isAuthenticated) return;
        void loadWalletBalances();
    }, [settingsAnchor, isAuthenticated, loadWalletBalances]);

    useEffect(() => {
        if (!placementHintCountdownActive) {
            placementHintEndsAtRef.current = null;
            return undefined;
        }
        const endsAt = placementHintEndsAtRef.current;
        if (endsAt == null) return undefined;

        const tick = () => {
            const left = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
            setPlacementHintCountdownSec((prev) => (prev === left ? prev : left));
        };
        tick();
        const timerId = window.setInterval(tick, 250);
        return () => window.clearInterval(timerId);
    }, [placementHintCountdownActive]);

    const handlePlacementHintClick = useCallback(async () => {
        if (isSolvedScatter || placementHintBusy || colorBasketMode) return;

        const gridN = puzzleGrid;
        const cellPxN = gridN > 0 ? BOARD_SIZE / gridN : 0;
        let pieceIndex = shuffledScatteredTray[0];
        if (pieceIndex == null && gridN > 0) {
            const misplaced = boardPiecesScatter.find(
                (piece) => !isBoardPieceAtHome(piece, gridN, cellPxN),
            );
            pieceIndex = misplaced?.pieceIndex;
        }
        if (pieceIndex == null || gridN <= 0) {
            setPlacementHintMessageSeverity("warning");
            setPlacementHintMessage(
                t("puzzle.jigsaw.placement-hint-none", "No pieces left to place."),
            );
            return;
        }

        if (!isAuthenticated) {
            setPlacementHintMessageSeverity("warning");
            setPlacementHintMessage(
                t("puzzle.jigsaw.placement-hint-sign-in", "Sign in to place a piece with coins."),
            );
            return;
        }

        const runId = placementHintRunIdRef.current + 1;
        placementHintRunIdRef.current = runId;
        setPlacementHintBusy(true);
        setPlacementHintMessage("");
        setPlacementHint(null);
        setPlacementHintCountdownActive(false);
        placementHintEndsAtRef.current = null;
        setPlacementHintCountdownSec(null);

        try {
            const spend = await HomeCreditService.consumeJigsawPlacePiece();
            if (typeof spend?.creditBalance === "number") {
                setWalletHintCredits(spend.creditBalance);
            } else {
                void loadWalletBalances();
            }
        } catch (err) {
            const message = err?.message || "";
            Logger.warn("Jigsaw place-piece coin spend failed:", message);
            setPlacementHintBusy(false);
            setPlacementHintMessageSeverity("warning");
            if (/insufficient/i.test(message)) {
                setPlacementHintMessage(
                    t("puzzle.jigsaw.placement-hint-insufficient", "Not enough coins."),
                );
            } else {
                setPlacementHintMessage(
                    t("puzzle.jigsaw.placement-hint-failed", "Could not place a piece. Please try again."),
                );
            }
            return;
        }

        if (placementHintRunIdRef.current !== runId) return;

        const homeX = (pieceIndex % gridN) * cellPxN;
        const homeY = Math.floor(pieceIndex / gridN) * cellPxN;
        const prev = boardPiecesScatter;
        const kept = prev.find((piece) => piece.pieceIndex === pieceIndex);
        const without = prev.filter((piece) => piece.pieceIndex !== pieceIndex);
        const newPiece = {
            id: kept?.id || newBoardPieceId(),
            pieceIndex,
            x: homeX,
            y: homeY,
        };
        const { pieces } = placeBoardPieceWithAttach(without, newPiece, gridN, cellPxN);
        setBoardPiecesScatter(pieces);
        setDragging(null);
        celebrateCorrectPlacement(without, pieces, pieceIndex, gridN, cellPxN);
        trackEvent("jigsaw_placement_hint_used", {
            puzzle_id: jigsawId,
            piece_count: 1,
            guest: false,
        });
        setPlacementHintMessageSeverity("success");
        setPlacementHintMessage(t("puzzle.jigsaw.placement-hint-placed", "Piece placed."));
        window.setTimeout(() => {
            if (placementHintRunIdRef.current !== runId) return;
            setPlacementHintMessage("");
        }, 1600);
        setPlacementHintBusy(false);
    }, [
        isSolvedScatter,
        placementHintBusy,
        colorBasketMode,
        puzzleGrid,
        shuffledScatteredTray,
        boardPiecesScatter,
        isBoardPieceAtHome,
        isAuthenticated,
        t,
        loadWalletBalances,
        jigsawId,
        celebrateCorrectPlacement,
    ]);

    const handleOpenFullImage = useCallback(() => {
        setSettingsAnchor(null);
        setImageDialogOpen(true);
    }, []);

    const handleToggleReferenceFromMenu = useCallback(() => {
        setSettingsAnchor(null);
        toggleReferenceOnScreen();
    }, [toggleReferenceOnScreen]);

    const handleBoardScaleChange = useCallback((scale) => {
        setBoardScale((prev) => {
            const prevSide = Math.round(prev * BOARD_SIZE);
            const nextSide = Math.round(scale * BOARD_SIZE);
            if (prevSide === nextSide) return prev;
            return nextSide / BOARD_SIZE;
        });
    }, []);

    const handleBoardDisplaySize = useCallback(({ height, width }) => {
        const side = Math.round(
            Math.min(
                Number.isFinite(width) && width > 0 ? width : Number.POSITIVE_INFINITY,
                Number.isFinite(height) && height > 0 ? height : Number.POSITIVE_INFINITY,
            ),
        );
        if (!Number.isFinite(side) || side <= 0) return;
        setBoardSquarePx((prev) => {
            if (prev == null) return side;
            if (Math.abs(prev - side) <= 1) return prev;
            return side;
        });
    }, []);

    const listTraySectionHeightSx =
        pieceViewMode === "list" ? { minHeight: listTrayDisplay.stripHeightPx } : {};

    const renderListTray = ({ vertical = false } = {}) => {
        const { circleOuterPx, stripHeightPx, trayScale } = listTrayDisplay;
        const pieceNodes = shuffledScatteredTray.map((pieceIndex) => {
            const isDraggingThis = dragging?.pieceIndex === pieceIndex && dragging?.fromScattered;
            const isHinted = placementHintTraySet.has(pieceIndex);
            return (
                <Box
                    key={pieceIndex}
                    data-jigsaw-tray-piece={pieceIndex}
                    sx={{
                        cursor: "grab",
                        opacity: isDraggingThis ? 0.3 : 1,
                        flexShrink: 0,
                        width: circleOuterPx,
                        height: circleOuterPx,
                        boxSizing: "border-box",
                        borderRadius: "50%",
                        overflow: "visible",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        touchAction: "none",
                        ...(isHinted
                            ? {
                                  animation: `${trayHintPulse} 1.1s ease-in-out infinite`,
                                  zIndex: 2,
                              }
                            : null),
                    }}
                    onMouseDown={(e) => {
                        const elRect = e.currentTarget.getBoundingClientRect();
                        const parentRect = scatteredRef.current?.getBoundingClientRect();
                        const scatterX = parentRect ? elRect.left - parentRect.left : 0;
                        const scatterY = parentRect ? elRect.top - parentRect.top : 0;
                        handleMouseDown(e, pieceIndex, {
                            fromScattered: true,
                            listTray: true,
                            scatterX,
                            scatterY,
                        });
                    }}
                    onTouchStart={(e) => {
                        const elRect = e.currentTarget.getBoundingClientRect();
                        const parentRect = scatteredRef.current?.getBoundingClientRect();
                        const scatterX = parentRect ? elRect.left - parentRect.left : 0;
                        const scatterY = parentRect ? elRect.top - parentRect.top : 0;
                        handleMouseDown(e, pieceIndex, {
                            fromScattered: true,
                            listTray: true,
                            scatterX,
                            scatterY,
                        });
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            width: 0,
                            height: 0,
                            overflow: "visible",
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                left: "50%",
                                top: "50%",
                                transform: `translate(-50%, -50%) scale(${trayScale})`,
                                transformOrigin: "center center",
                                lineHeight: 0,
                            }}
                        >
                            <PieceVisual
                                pieceIndex={pieceIndex}
                                imageUrl={imageUrl}
                                grid={grid}
                                paths={paths}
                                showGridCoords={false}
                                anchorToCell={false}
                            />
                        </Box>
                    </Box>
                </Box>
            );
        });

        if (vertical) {
            const boardSide =
                playBoardSquarePx > 0
                    ? playBoardSquarePx
                    : boardSquarePx != null && boardSquarePx > 0
                      ? boardSquarePx
                      : BOARD_SIZE * boardScale;
            return (
                <PieceTrayScrollColumn
                    containerRef={scatteredRef}
                    columnWidthPx={circleOuterPx}
                    stripWidthPx={circleOuterPx}
                    maxHeightPx={boardSide > 0 ? boardSide : undefined}
                    sx={{
                        position: "relative",
                        zIndex: trayAreaZIndex,
                        isolation: "isolate",
                        ...(trayFrontActive
                            ? {
                                  bgcolor: TRAY_PLATE_BG,
                                  borderRadius: 1,
                                  py: 0.5,
                                  boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
                              }
                            : null),
                    }}
                >
                    {pieceNodes}
                    {shuffledScatteredTray.length === 0 ? (
                        <Typography
                            variant="caption"
                            sx={{ py: 2, px: 0.5, textAlign: "center", color: PLAY_TOOLBAR_FG }}
                        >
                            {t("puzzle.jigsaw.all-pieces-placed", "All pieces placed")}
                        </Typography>
                    ) : null}
                </PieceTrayScrollColumn>
            );
        }

        return (
        <Box
            ref={scatteredRef}
            sx={{
                width: "100%",
                maxWidth: "100%",
                flexShrink: 0,
                alignSelf: "flex-start",
                minHeight: stripHeightPx,
                flex: mobilePortraitCompact ? "0 0 auto" : undefined,
                position: "relative",
                zIndex: trayAreaZIndex,
                boxSizing: "border-box",
                overflowX: "auto",
                overflowY: "hidden",
                WebkitOverflowScrolling: "touch",
                isolation: "isolate",
                scrollbarGutter: "stable",
                ...(trayFrontActive
                    ? {
                          bgcolor: TRAY_PLATE_BG,
                          borderRadius: 1,
                          py: 0.5,
                          boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
                      }
                    : null),
            }}
        >
            <Box
                sx={
                    isPromoPortraitCapture
                        ? {
                              display: "grid",
                              gridTemplateRows: `repeat(${PROMO_PORTRAIT_TRAY_ROWS}, ${circleOuterPx}px)`,
                              gridAutoFlow: "column",
                              gridAutoColumns: `${circleOuterPx}px`,
                              alignItems: "center",
                              justifyItems: "center",
                              columnGap: "8px",
                              rowGap: "8px",
                              width: "max-content",
                              minWidth: "100%",
                              height: stripHeightPx,
                              boxSizing: "border-box",
                          }
                        : {
                              display: "flex",
                              flexDirection: "row",
                              flexWrap: "nowrap",
                              alignItems: "center",
                              width: "max-content",
                              minWidth: "100%",
                              height: circleOuterPx,
                              flexShrink: 0,
                              gap: 0,
                          }
                }
            >
            {pieceNodes}
            {shuffledScatteredTray.length === 0 && (
                <Typography variant="caption" sx={{ py: 2, px: 1, color: PLAY_TOOLBAR_FG }}>
                    {t("puzzle.jigsaw.all-pieces-placed", "All pieces placed")}
                </Typography>
            )}
            </Box>
        </Box>
        );
    };

    const renderScatteredTray = () => {
        const measuredSide = boardSquarePxRef.current;
        const traySidePx =
            playBoardSquarePx > 0
                ? playBoardSquarePx
                : measuredSide != null && measuredSide > 0
                  ? measuredSide
                  : BOARD_SIZE * boardScale;
        const trayMaxHeight = mobilePortraitCompact ? `${MOBILE_PORTRAIT_TRAY_MAX_VH}dvh` : undefined;

        return (
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    overflow: "hidden",
                    position: "relative",
                    zIndex: trayAreaZIndex,
                    ...(trayMaxHeight ? { maxHeight: trayMaxHeight } : {}),
                }}
            >
                <Box
                    sx={{
                        width: traySidePx,
                        height: traySidePx,
                        flexShrink: 0,
                        overflow: "hidden",
                        position: "relative",
                    }}
                >
                    <Box
                        ref={scatteredRef}
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: BOARD_SIZE,
                            height: BOARD_SIZE,
                            transform: `scale(${trayPieceScale})`,
                            transformOrigin: "top left",
                            bgcolor: "grey.900",
                            borderRadius: 1,
                            overflow: "visible",
                        }}
                    >
                        {[...piecesScatteredTray]
                            .sort(
                                (a, b) =>
                                    (pieceFocusOrder[a] ?? a) - (pieceFocusOrder[b] ?? b),
                            )
                            .map((pieceIndex) => {
                            const pos = scatterPositions[pieceIndex];
                            if (!pos) return null;
                            const isDraggingThis =
                                dragging?.pieceIndex === pieceIndex && dragging?.fromScattered;
                            const isHinted = placementHintTraySet.has(pieceIndex);
                            return (
                                <div
                                    key={pieceIndex}
                                    data-jigsaw-tray-piece={pieceIndex}
                                    onMouseDown={(e) =>
                                        handleMouseDown(e, pieceIndex, {
                                            fromScattered: true,
                                            scatterX: pos.x,
                                            scatterY: pos.y,
                                        })
                                    }
                                    onTouchStart={(e) =>
                                        handleMouseDown(e, pieceIndex, {
                                            fromScattered: true,
                                            scatterX: pos.x,
                                            scatterY: pos.y,
                                        })
                                    }
                                    style={{
                                        position: "absolute",
                                        left: pos.x,
                                        top: pos.y,
                                        cursor: "grab",
                                        opacity: isDraggingThis ? 0.3 : 1,
                                        zIndex: isHinted
                                            ? 10000
                                            : (pieceFocusOrder[pieceIndex] ?? pieceIndex),
                                        touchAction: "none",
                                        borderRadius: 8,
                                        boxShadow: isHinted
                                            ? "0 0 0 3px rgba(59, 166, 102, 0.95), 0 0 14px rgba(59, 166, 102, 0.7)"
                                            : undefined,
                                    }}
                                >
                                    <PieceVisual
                                        pieceIndex={pieceIndex}
                                        imageUrl={imageUrl}
                                        grid={grid}
                                        paths={paths}
                                        showGridCoords={false}
                                    />
                                </div>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        );
    };

    const boardCanvasFitSize = mobilePortraitCompact
        ? `min(100%, calc(100vw - 24px), ${MOBILE_PORTRAIT_BOARD_MAX_VH}dvh)`
        : undefined;
    const boardCanvasMaxSize =
        boardCanvasFitSize && boardZoom !== 1
            ? `calc(${boardCanvasFitSize} * ${boardZoom})`
            : boardCanvasFitSize;

    const boardCanvasWrapperSx = {
        width: boardZoom !== 1 ? `${boardZoom * 100}%` : "100%",
        minWidth: 0,
        maxWidth: boardZoom > 1 ? "none" : "100%",
        overflow: "visible",
        aspectRatio: "1 / 1",
        ...(boardZoom > 1 ? { willChange: "transform" } : null),
        ...(boardCanvasMaxSize ? { maxWidth: boardCanvasMaxSize, maxHeight: boardCanvasMaxSize } : {}),
    };

    const renderScatterBoardBlock = ({
        squareLayout = false,
        fitParent = false,
        squareSx = {},
        squareMaxSize,
        explicitDisplaySidePx,
    } = {}) => {
        if (isSolvedScatter && imageUrl) {
            const solvedImage = (
                <PuzzleSolvedOriginalImage
                    imageUrl={imageUrl}
                    alt={t("puzzle.jigsaw.reference-image")}
                    sx={{ borderRadius: 1 }}
                />
            );
            if (squareLayout) {
                return (
                    <Box sx={{ ...equalSplitSquareSx, ...squareSx }}>
                        <Box
                            sx={{
                                width: "100%",
                                height: "100%",
                                minWidth: 0,
                                minHeight: 0,
                                bgcolor: "grey.900",
                                borderRadius: 1,
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {solvedImage}
                        </Box>
                    </Box>
                );
            }
            return (
                <Box sx={{ width: "100%", minWidth: 0, flexShrink: 0 }}>
                    <Box sx={{ ...boardCanvasWrapperSx, bgcolor: "grey.900", borderRadius: 1 }}>
                        {solvedImage}
                    </Box>
                </Box>
            );
        }

        const canvas = (
            <CanvasBoard
                boardRef={boardRefScatter}
                boardPieces={boardPiecesScatter}
                grid={grid}
                imageUrl={imageUrl}
                paths={paths}
                svgPathFallback={svgPathFallback}
                dragging={dragging}
                showDragOutline={false}
                placedCount={placedCountScatter}
                total={total}
                isSolved={isSolvedScatter}
                showPieceGridCoords={false}
                showHeader={false}
                fitParent={fitParent}
                squareMaxSize={squareMaxSize}
                explicitDisplaySidePx={explicitDisplaySidePx}
                onScaleChange={handleBoardScaleChange}
                onDisplaySizeChange={handleBoardDisplaySize}
                onBoardPieceMouseDown={(e, pieceIndex, meta) => {
                    if (boardZoom > 1) return;
                    handleMouseDown(e, pieceIndex, { boardLane: "scatter", ...meta });
                }}
                pieceFocusOrder={pieceFocusOrder}
                matPreset={boardMat}
                matColor={boardMat.hex}
                grooveStyle={boardGrooveStyle}
                activePieceShines={activePieceShines}
                lineMatchFx={lineMatchFx}
                onLineMatchFxComplete={clearLineMatchFx}
                placementHintPieceIndices={placementHintBoardIndices}
                showDieLines={showDieLines}
            />
        );

        if (squareLayout) {
            return (
                <Box sx={{ ...equalSplitSquareSx, ...squareSx }}>
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            minWidth: 0,
                            minHeight: 0,
                            position: "relative",
                            overflow: "visible",
                        }}
                    >
                        {canvas}
                    </Box>
                </Box>
            );
        }

        return (
            <Box sx={{ width: "100%", minWidth: 0, overflow: "visible", flexShrink: 0 }}>
                <Box sx={boardCanvasWrapperSx}>{canvas}</Box>
            </Box>
        );
    };

    const renderReferencePanel = (extraSx = {}) => {
        if (!referenceOnScreen || !imageUrl || isSolvedScatter) return null;
        return (
            <Box sx={{ ...referencePanelSx, ...extraSx }}>
                <IconButton
                    size="small"
                    onClick={hideReferenceOnScreen}
                    aria-label={t("puzzle.jigsaw.hide-on-screen")}
                    sx={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        zIndex: 2,
                        bgcolor: "rgba(0,0,0,0.45)",
                        color: "common.white",
                        p: 0.25,
                        "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                    }}
                >
                    <CloseIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
                <Box
                    sx={{
                        position: "absolute",
                        top: 2,
                        left: 2,
                        zIndex: 2,
                        display: "flex",
                        gap: 0.25,
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={handleOpenFullImage}
                        aria-label={t("puzzle.jigsaw.open-full-image", "Open full image")}
                        sx={{
                            bgcolor: "rgba(0,0,0,0.45)",
                            color: "common.white",
                            p: 0.25,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                        }}
                    >
                        <OpenInNewIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => stepReferenceImageZoom(-1)}
                        disabled={referenceImageZoom <= REFERENCE_IMAGE_ZOOM_MIN + 0.001}
                        aria-label={t("puzzle.jigsaw.zoom-out", "Zoom out")}
                        sx={{
                            bgcolor: "rgba(0,0,0,0.45)",
                            color: "common.white",
                            p: 0.25,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                            "&.Mui-disabled": { color: "rgba(255,255,255,0.45)" },
                        }}
                    >
                        <ZoomOutIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => stepReferenceImageZoom(1)}
                        disabled={referenceImageZoom >= REFERENCE_IMAGE_ZOOM_MAX - 0.001}
                        aria-label={t("puzzle.jigsaw.zoom-in", "Zoom in")}
                        sx={{
                            bgcolor: "rgba(0,0,0,0.45)",
                            color: "common.white",
                            p: 0.25,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                            "&.Mui-disabled": { color: "rgba(255,255,255,0.45)" },
                        }}
                    >
                    <ZoomInIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
                </Box>
                <Box
                    ref={referenceImageRef}
                    sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 0,
                        willChange: "transform",
                    }}
                >
                    <Box
                        component="img"
                        src={imageUrl}
                        alt={t("puzzle.jigsaw.reference-image")}
                        referrerPolicy="no-referrer"
                        sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                    />
                    <JigsawDieLineOverlay
                        pieces={paths}
                        svgPathFallback={svgPathFallback}
                        bevel3d
                        opacity={0.85}
                        zIndex={1}
                        strokeWidth={1}
                    />
                </Box>
            </Box>
        );
    };

    /** Flex/grid row: board with tray stacked directly underneath. */
    const renderDesktopPlaySurface = ({ hideTray = false } = {}) => {
        const cellPx = BOARD_SIZE / grid;
        const trayLayoutPx = hideTray || trayBesideBoard
            ? 0
            : listTrayMetrics(cellPx, playBoardSquarePx > 0 ? playBoardSquarePx / BOARD_SIZE : 1)
                  .stripHeightPx + 8;
        const rootRect = playRootRef.current?.getBoundingClientRect();
        const availBoardPx =
            rootRect && rootRect.height > 0
                ? Math.max(0, rootRect.height - DESKTOP_PLAY_CHROME_PX - trayLayoutPx)
                : null;
        const desktopBoardMaxHeight =
            availBoardPx != null && availBoardPx > 0
                ? availBoardPx
                : hideTray
                  ? `calc(100% - ${DESKTOP_PLAY_CHROME_OFFSET})`
                  : `calc(100% - ${DESKTOP_PLAY_CHROME_OFFSET} - ${trayLayoutPx}px)`;
        const zoomedBoardPx =
            playBoardSquarePx > 0 ? Math.round(playBoardSquarePx * boardZoom) : 0;
        const zoomedOutOfFrame = boardZoom > 1;
        const boardSquareSx =
            zoomedBoardPx > 0
                ? {
                      ...explicitPlaySquareSx(zoomedBoardPx, desktopBoardMaxHeight),
                      ...(zoomedOutOfFrame
                          ? { maxWidth: "none", maxHeight: "none", willChange: "transform" }
                          : null),
                  }
                : { width: "100%", minWidth: 0, maxWidth: "100%", aspectRatio: "1 / 1", maxHeight: desktopBoardMaxHeight };
        const boardSquareMaxSize = zoomedBoardPx > 0 ? zoomedBoardPx : desktopBoardMaxHeight;
        /** Zoomed-in board stays in the fitted frame; drag the board to pan. */
        const boardScrollSx = zoomedOutOfFrame
            ? {
                  width: playBoardSquarePx > 0 ? playBoardSquarePx : "100%",
                  height: playBoardSquarePx > 0 ? playBoardSquarePx : "100%",
                  overflow: "auto",
                  maxWidth: "100%",
                  maxHeight:
                      availBoardPx != null && availBoardPx > 0 ? availBoardPx : undefined,
                  flexShrink: 0,
                  cursor: "grab",
                  touchAction: "pan-x pan-y",
                  overscrollBehavior: "contain",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "thin",
              }
            : null;
        const trayWidth =
            playBoardSquarePx > 0 ? { width: playBoardSquarePx, maxWidth: "100%" } : { width: "100%" };
        const showReferencePanel = referenceOnScreen && Boolean(imageUrl) && !isSolvedScatter;
        const referenceTrayColPx = Math.ceil((listTrayDisplay.circleOuterPx || 72) + 12);
        const referenceBeside = trayBesideBoard && !hideTray;
        const referenceGroupW = referenceBeside
            ? playBoardSquarePx + 16 + referenceTrayColPx
            : playBoardSquarePx;
        const referenceTrayStripPx =
            referenceBeside || hideTray ? 0 : (listTrayDisplay.stripHeightPx || 0) + 8;
        const referenceGroupH = playBoardSquarePx + referenceTrayStripPx;
        const referenceLeftSpace =
            playSurfaceBox.w > 0
                ? Math.max(0, Math.floor((playSurfaceBox.w - referenceGroupW) / 2))
                : 0;
        const referenceFramePad = 8;
        const referenceAvailW = Math.max(0, referenceLeftSpace - referenceFramePad * 2);
        const referenceFrameTopBound =
            playSurfaceBox.h > 0
                ? Math.max(referenceFramePad, Math.floor((playSurfaceBox.h - referenceGroupH) / 2))
                : referenceFramePad;
        const referenceAvailH = Math.min(
            referenceGroupH,
            Math.max(0, playSurfaceBox.h - referenceFrameTopBound - referenceFramePad),
        );
        const referenceOverlay =
            showReferencePanel && referenceAvailW >= 72 && referenceAvailH >= 72 ? (
            <Box
                ref={referenceClipRef}
                sx={{
                    position: "absolute",
                    left: referenceFramePad,
                    top: referenceFrameTopBound,
                    width: referenceAvailW,
                    height: referenceAvailH,
                    zIndex: 4,
                    pointerEvents: "auto",
                    overflow: "hidden",
                    touchAction: "none",
                }}
            >
                {renderReferencePanel({
                    width: "100%",
                    height: "auto",
                    aspectRatio: "1 / 1",
                    overflow: "visible",
                    bgcolor: "transparent",
                    boxShadow: "none",
                })}
            </Box>
        ) : null;

        if (trayBesideBoard && !hideTray) {
            const trayColPx = Math.ceil((listTrayDisplay.circleOuterPx || 72) + 12);
            const boardSide = playBoardSquarePx > 0 ? playBoardSquarePx : zoomedBoardPx;
            return (
                <Box
                    ref={playSurfaceRef}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        visibility: playBoardSquarePx > 0 ? "visible" : "hidden",
                        position: "relative",
                    }}
                >
                    {referenceOverlay}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "16px",
                            maxWidth: "100%",
                            maxHeight: "100%",
                        }}
                    >
                        <Box
                            ref={attachPuzzleColumnRef}
                            sx={{
                                flexShrink: 0,
                                minWidth: 0,
                                alignSelf: "center",
                                ...boardScrollSx,
                            }}
                        >
                            {renderScatterBoardBlock({
                                squareLayout: true,
                                fitParent: false,
                                squareSx: boardSquareSx,
                                squareMaxSize: boardSquareMaxSize,
                            })}
                        </Box>
                        <Box
                            sx={{
                                flexShrink: 0,
                                width: trayColPx,
                                minWidth: trayColPx,
                                height: boardSide > 0 ? boardSide : "100%",
                                maxHeight: "100%",
                                display: "flex",
                                alignItems: "stretch",
                                position: "relative",
                                zIndex: trayAreaZIndex,
                            }}
                        >
                            {isScatteredView ? renderScatteredTray() : renderListTray({ vertical: true })}
                        </Box>
                    </Box>
                </Box>
            );
        }

        return (
            <Box
                ref={playSurfaceRef}
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gridTemplateRows: "auto auto",
                    gap: 1,
                    width: "100%",
                    height: "100%",
                    alignContent: "center",
                    justifyContent: "center",
                    alignItems: "center",
                    visibility: playBoardSquarePx > 0 ? "visible" : "hidden",
                    position: "relative",
                }}
            >
                {referenceOverlay}
                <Box
                    ref={attachPuzzleColumnRef}
                    sx={{
                        gridColumn: 1,
                        gridRow: 1,
                        minWidth: 0,
                        alignSelf: "start",
                        ...boardScrollSx,
                    }}
                >
                    {renderScatterBoardBlock({
                        squareLayout: true,
                        fitParent: false,
                        squareSx: boardSquareSx,
                        squareMaxSize: boardSquareMaxSize,
                    })}
                </Box>
                {!hideTray ? (
                    <Box
                        sx={{
                            ...listTraySectionSx,
                            ...listTraySectionHeightSx,
                            gridColumn: 1,
                            gridRow: 2,
                            alignSelf: "start",
                            mt: 0,
                            ...trayWidth,
                            position: "relative",
                            zIndex: trayAreaZIndex,
                        }}
                    >
                        {isScatteredView ? renderScatteredTray() : renderListTray()}
                    </Box>
                ) : null}
            </Box>
        );
    };

    /** 16:9 capture — board + vertical tray fill the viewport (OBS-safe). */
    const renderCaptureLandscapePlaySurface = ({ hideTray = false } = {}) => {
        const boardSide =
            playBoardSquarePx > 0
                ? playBoardSquarePx
                : grid > 0
                  ? computeCapture1080BoardSidePx(grid)
                  : null;
        const boardSquareSx =
            boardSide != null
                ? {
                      width: boardSide,
                      height: boardSide,
                      maxWidth: boardSide,
                      maxHeight: boardSide,
                      minWidth: 0,
                      flexShrink: 0,
                      overflow: "visible",
                      boxSizing: "border-box",
                  }
                : {
                      height: "100%",
                      maxHeight: "100%",
                      aspectRatio: "1 / 1",
                      width: "auto",
                      maxWidth: "100%",
                      minWidth: 0,
                  };
        const trayColPx = Math.ceil(
            (listTrayDisplay.circleOuterPx || 48) + CAPTURE_TRAY_EDGE_PAD_PX,
        );

        return (
            <Box
                ref={playSurfaceRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    boxSizing: "border-box",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "stretch",
                        justifyContent: "center",
                        gap: `${CAPTURE_TRAY_GAP_PX}px`,
                        flexShrink: 0,
                        height: boardSide != null ? boardSide : "100%",
                        maxHeight: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                    }}
                >
                    <Box
                        ref={attachPuzzleColumnRef}
                        sx={{
                            flexShrink: 0,
                            width: boardSide != null ? boardSide : "auto",
                            height: boardSide != null ? boardSide : "100%",
                            maxWidth: boardSide != null ? boardSide : "100%",
                            maxHeight: "100%",
                            minWidth: 0,
                            minHeight: 0,
                            position: "relative",
                            overflow: "visible",
                        }}
                    >
                        {renderScatterBoardBlock({
                            // Explicit px scale — OBS CEF mis-evaluates container-query (cqw) scale.
                            squareLayout: true,
                            fitParent: false,
                            squareSx: boardSquareSx,
                            squareMaxSize: boardSide != null ? boardSide : undefined,
                            explicitDisplaySidePx: boardSide != null ? boardSide : undefined,
                        })}
                    </Box>
                    {!hideTray ? (
                        <Box
                            sx={{
                                flexShrink: 0,
                                width: trayColPx,
                                minWidth: trayColPx,
                                maxWidth: trayColPx,
                                height: boardSide != null ? boardSide : "100%",
                                maxHeight: "100%",
                                overflow: "hidden",
                                alignSelf: "stretch",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-start",
                                position: "relative",
                                zIndex: trayAreaZIndex,
                            }}
                        >
                            {isScatteredView
                                ? renderScatteredTray()
                                : renderListTray({ vertical: true })}
                        </Box>
                    ) : null}
                </Box>
            </Box>
        );
    };

    const renderPlayGridContent = () => {
        if (colorBasketMode) {
            if (colorBasketSession.loading) {
                return (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6, flex: 1 }}>
                        <CircularProgress sx={{ color: "#3BA666" }} />
                    </Box>
                );
            }
            if (colorBasketSession.error) {
                return (
                    <Typography color="error" sx={{ px: 2, py: 2 }}>
                        {colorBasketSession.error}
                    </Typography>
                );
            }
            if (selectedColorBasket && imageUrl && paths && puzzleGrid > 0) {
                return (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            width: "100%",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <AdminColorBasketPlayPanel
                            basket={selectedColorBasket}
                            allBaskets={colorBasketSession.baskets}
                            basketCount={colorBasketSession.basketCount}
                            jigsawId={jigsawId}
                            grid={puzzleGrid}
                            imageUrl={imageUrl}
                            paths={paths}
                            svgPathFallback={svgPathFallback}
                            hideMainBoard
                            hideCloseButton
                            simpleMode
                            promoCapture={isPromoPortraitCapture}
                            isAdminRole={isAdminRole}
                            onBackToMainPlay={handleBackFromBoard}
                            onMainBoardSynced={applyMainBoardFromBasketSync}
                            soundEffectsEnabled={soundEffectsEnabled}
                            flushRef={colorBasketFlushRef}
                            puzzleTitle={title}
                            toolbarLeading={
                                <Tooltip title={t("puzzle.jigsaw.main-play", "Main Play")} arrow>
                                    <IconButton
                                        size="medium"
                                        onClick={handleBackFromBoard}
                                        sx={{ ...toolbarIconSx }}
                                        aria-label={t("puzzle.jigsaw.main-play", "Main Play")}
                                    >
                                        <ArrowBackIcon fontSize="medium" />
                                    </IconButton>
                                </Tooltip>
                            }
                            toolbarMid={
                                <Box
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.25,
                                        flexShrink: 0,
                                        ml: 0.5,
                                        ...(isPromoPortraitCapture
                                            ? {
                                                  "& .MuiTypography-root": {
                                                      fontSize: "2rem",
                                                      fontWeight: 800,
                                                  },
                                              }
                                            : null),
                                    }}
                                >
                                    <PlayElapsedTimer
                                        persistKey={
                                            jigsawId ? `jigsaw-play-elapsed:${jigsawId}` : null
                                        }
                                        running={!isSolvedScatter && !timerEffectivelyPaused}
                                        ariaLabel={t("puzzle.play-elapsed-time", "Elapsed time")}
                                    />
                                    {!isSolvedScatter ? (
                                        <Tooltip
                                            title={
                                                timerEffectivelyPaused
                                                    ? t("puzzle.jigsaw.resume-timer", "Resume timer")
                                                    : t("puzzle.jigsaw.pause-timer", "Pause timer")
                                            }
                                            arrow
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    if (visibilityPaused) {
                                                        continuePlaying();
                                                        setTimerPaused((prev) => {
                                                            if (
                                                                prev
                                                                && timerPauseStartedAtRef.current != null
                                                                && playStartMsRef.current != null
                                                            ) {
                                                                playStartMsRef.current +=
                                                                    Date.now()
                                                                    - timerPauseStartedAtRef.current;
                                                                timerPauseStartedAtRef.current = null;
                                                            }
                                                            return false;
                                                        });
                                                        return;
                                                    }
                                                    handleToggleTimerPause();
                                                }}
                                                sx={{
                                                    flexShrink: 0,
                                                    ml: -0.75,
                                                    ...toolbarIconSx,
                                                }}
                                                aria-label={
                                                    timerEffectivelyPaused
                                                        ? t(
                                                              "puzzle.jigsaw.resume-timer",
                                                              "Resume timer",
                                                          )
                                                        : t(
                                                              "puzzle.jigsaw.pause-timer",
                                                              "Pause timer",
                                                          )
                                                }
                                                aria-pressed={timerEffectivelyPaused}
                                            >
                                                {timerEffectivelyPaused ? (
                                                    <PlayArrowIcon fontSize="small" />
                                                ) : (
                                                    <PauseIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    ) : null}
                                    <Tooltip
                                        title={t(
                                            "puzzle.jigsaw.sweep-misplaced",
                                            "Remove pieces that are not in the correct place",
                                        )}
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={handleSweepMisplacedFromBoard}
                                                disabled={!hasMisplacedOnBoard}
                                                sx={{
                                                    flexShrink: 0,
                                                    color: "#3BA666",
                                                    ...toolbarIconSx,
                                                }}
                                                aria-label={t(
                                                    "puzzle.jigsaw.sweep-misplaced",
                                                    "Remove pieces that are not in the correct place",
                                                )}
                                            >
                                                <CleaningServicesIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            }
                        />
                    </Box>
                );
            }
            return (
                <Typography sx={{ px: 2, py: 2, color: PLAY_TOOLBAR_FG }}>
                    {t("puzzle.jigsaw.basket-not-ready", "Basket is not ready to play.")}
                </Typography>
            );
        }

        if (isCaptureLandscape) {
            if (isSolvedScatter && imageUrl) {
                return renderCaptureLandscapePlaySurface({ hideTray: true });
            }
            return renderCaptureLandscapePlaySurface();
        }

        if (isSolvedScatter && imageUrl) {
            return renderDesktopPlaySurface({ hideTray: true });
        }

        if (mobilePortraitCompact) {
            const showReferencePanel = referenceOnScreen && Boolean(imageUrl) && !isSolvedScatter;
            return (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-start",
                        gap: 0.75,
                        flex: 1,
                        minHeight: 0,
                        width: "100%",
                        minWidth: 0,
                        position: "relative",
                    }}
                >
                    {showReferencePanel ? (
                        <Box
                            ref={referenceClipRef}
                            sx={{
                                position: "absolute",
                                left: 4,
                                top: 4,
                                width: 160,
                                maxWidth: 160,
                                zIndex: 4,
                                overflow: "hidden",
                                touchAction: "none",
                            }}
                        >
                            {renderReferencePanel()}
                        </Box>
                    ) : null}
                    <Box
                        sx={{
                            flex: "1 1 0",
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "stretch",
                        }}
                    >
                        <Box
                            ref={attachPuzzleColumnRef}
                            sx={{
                                flexShrink: 0,
                                minWidth: 0,
                                position: "relative",
                                zIndex: 1,
                                ...mobilePortraitBoardCapSx,
                                ...(boardZoom > 1
                                    ? {
                                          overflow: "auto",
                                          maxHeight: `min(calc(100vw - 24px), ${MOBILE_PORTRAIT_BOARD_MAX_VH}dvh)`,
                                          cursor: "grab",
                                          touchAction: "pan-x pan-y",
                                          overscrollBehavior: "contain",
                                          WebkitOverflowScrolling: "touch",
                                      }
                                    : null),
                            }}
                        >
                            {renderScatterBoardBlock()}
                        </Box>
                        <Box
                            sx={{
                                ...listTraySectionSx,
                                ...listTraySectionHeightSx,
                                flexShrink: 0,
                                mt: 0.75,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "stretch",
                                position: "relative",
                                zIndex: trayAreaZIndex,
                            }}
                        >
                            {pieceViewMode === "list" ? renderListTray() : renderScatteredTray()}
                        </Box>
                    </Box>
                </Box>
            );
        }

        return renderDesktopPlaySurface();
    };

    return (
        <>
            <PlayBoardSeoHead
                kind="jigsaw"
                storyId={storyId}
                boardId={jigsawId}
                story={story}
                board={selectedTemplate}
                noindex={playBoardSeoNoindex}
            />
        <Box
            ref={playRootRef}
            sx={{
                position: isCaptureLandscape ? "fixed" : "relative",
                isolation: "isolate",
                p: isCaptureLandscape
                    ? `${CAPTURE_PLAY_PAD_PX}px`
                    : { xs: isMobilePortrait ? "6px 6px 4px" : "8px 8px 4px" },
                maxWidth: "none",
                mx: 0,
                minWidth: 0,
                overflowX: "hidden",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
                bgcolor: SITE_BG,
                ...(isMobilePortrait ? { maxWidth: "100%" } : {}),
                ...(isCaptureLandscape
                    ? {
                          top: 0,
                          left: 0,
                          width: CAPTURE_FRAME_W,
                          height: CAPTURE_FRAME_H,
                          maxWidth: CAPTURE_FRAME_W,
                          maxHeight: CAPTURE_FRAME_H,
                          minWidth: CAPTURE_FRAME_W,
                          minHeight: CAPTURE_FRAME_H,
                      }
                    : {
                          width: "100%",
                          ...(docViewportPx.h > 0 && !mobilePortraitCompact
                              ? {
                                    height: docViewportPx.h,
                                    maxHeight: docViewportPx.h,
                                    minHeight: docViewportPx.h,
                                }
                              : {
                                    height: mobilePortraitCompact
                                        ? MOBILE_PORTRAIT_PLAY_HEIGHT
                                        : JIGSAW_PLAY_VIEWPORT_HEIGHT,
                                    maxHeight: mobilePortraitCompact
                                        ? MOBILE_PORTRAIT_PLAY_HEIGHT
                                        : JIGSAW_PLAY_VIEWPORT_HEIGHT,
                                }),
                      }),
            }}
        >
            <SlidingPlayMistBackground variant="night" />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : !group || !selectedTemplate ? (
                <Typography sx={{ color: PLAY_TOOLBAR_FG }}>Puzzle template not found.</Typography>
            ) : boardLockReason ? (
                <>
                    <Box sx={{ mb: 2 }}>
                        <IconButton
                            size="small"
                            onClick={handleBackFromBoard}
                            sx={{ color: "#3BA666", ...toolbarIconSx }}
                            aria-label={t("common.back", "Back")}
                        >
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <PuzzleAccessGate
                        isAuthenticated={isAuthenticated}
                        userPayPlan={userPayPlan}
                        onUpgradeClick={() => setSubscriptionModalOpen(true)}
                        puzzleTitle={getStoryTitle(group?.story)}
                        onBackClick={handleBackFromBoard}
                    />
                    <SubscriptionDialog
                        open={subscriptionModalOpen}
                        onClose={() => setSubscriptionModalOpen(false)}
                        onSubscribe={() => {
                            setSubscriptionModalOpen(false);
                            window.location.reload();
                        }}
                        triggerReason="puzzle"
                        userSubscription={userPayPlan || "free"}
                    />
                </>
            ) : needsPremiumCreditCharge && premiumCreditStatus !== "ok"
                && premiumCreditStatus !== "insufficient" && premiumCreditStatus !== "error" ? (
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 0,
                        py: 4,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : needsPremiumCreditCharge
                && (premiumCreditStatus === "insufficient" || premiumCreditStatus === "error") ? (
                <>
                    <Box sx={{ mb: 2 }}>
                        <IconButton
                            size="small"
                            onClick={handleBackFromBoard}
                            sx={{ color: "#3BA666", ...toolbarIconSx }}
                            aria-label={t("common.back", "Back")}
                        >
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <PuzzleAccessGate
                        isAuthenticated={isAuthenticated}
                        userPayPlan={userPayPlan}
                        onUpgradeClick={() => setSubscriptionModalOpen(true)}
                        puzzleTitle={getStoryTitle(group?.story)}
                        onBackClick={handleBackFromBoard}
                    />
                    <Dialog
                        open={creditAlertOpen}
                        onClose={() => setCreditAlertOpen(false)}
                        maxWidth="xs"
                        fullWidth
                        aria-labelledby="jigsaw-credit-alert-title"
                    >
                        <DialogTitle id="jigsaw-credit-alert-title">
                            {creditAlertInsufficient
                                ? t("core.puzzleAccess.insufficientCredits", "Insufficient credits")
                                : t("core.puzzleAccess.creditChargeError", "Could not spend credits.")}
                        </DialogTitle>
                        <DialogContent>
                            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                                {creditAlertMessage}
                            </Typography>
                            {creditAlertInsufficient ? (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 1.5 }}
                                >
                                    {t(
                                        "core.puzzleAccess.insufficientCreditsHint",
                                        "Get more play credits to unlock this puzzle.",
                                    )}
                                </Typography>
                            ) : null}
                        </DialogContent>
                        <DialogActions sx={{ px: 2, pb: 2 }}>
                            <Button onClick={() => setCreditAlertOpen(false)}>
                                {t("common.ok", "OK")}
                            </Button>
                            {creditAlertInsufficient ? (
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        setCreditAlertOpen(false);
                                        setSubscriptionModalOpen(true);
                                    }}
                                    sx={{ bgcolor: "#3BA666", "&:hover": { bgcolor: "#2e8a52" } }}
                                >
                                    {t("core.puzzleAccess.getCredits", "Get credits")}
                                </Button>
                            ) : null}
                        </DialogActions>
                    </Dialog>
                    <SubscriptionDialog
                        open={subscriptionModalOpen}
                        onClose={() => setSubscriptionModalOpen(false)}
                        onSubscribe={() => {
                            setSubscriptionModalOpen(false);
                            window.location.reload();
                        }}
                        triggerReason="puzzle"
                        userSubscription={userPayPlan || "free"}
                    />
                </>
            ) : (
                <>
                    {!colorBasketMode ? (
                    <Box
                        ref={isCaptureLandscape ? captureToolbarRef : undefined}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                                isCaptureLandscape || isPromoPortraitCapture
                                    ? "center"
                                    : "space-between",
                            gap: isPromoPortraitCapture ? 2 : isCaptureLandscape ? 1.5 : 1,
                            mb: isPromoPortraitCapture ? 2 : isCaptureLandscape ? 0.5 : 1.5,
                            mt: 0,
                            minWidth: 0,
                            flexShrink: 0,
                            width: "100%",
                            ...(isPromoPortraitCapture
                                ? {
                                      flexWrap: "wrap",
                                      rowGap: 1.5,
                                  }
                                : null),
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                minWidth: 0,
                                flex: "1 1 auto",
                                pr: 1,
                            }}
                        >
                            <IconButton
                                size="medium"
                                onClick={handleBackFromBoard}
                                sx={{ ...toolbarIconSx, flexShrink: 0 }}
                                aria-label={t("common.back", "Back")}
                            >
                                <ArrowBackIcon fontSize="medium" />
                            </IconButton>
                            <Typography
                                variant="subtitle1"
                                component="h1"
                                noWrap
                                title={title || t("nav.jigsaw", "Jigsaw")}
                                sx={{
                                    fontWeight: isPromoPortraitCapture ? 800 : 700,
                                    color: PLAY_TOOLBAR_FG,
                                    minWidth: 0,
                                    lineHeight: 1.2,
                                    fontSize: isPromoPortraitCapture
                                        ? "1.85rem"
                                        : { xs: "1.05rem", sm: "1.15rem" },
                                }}
                            >
                                {title || t("nav.jigsaw", "Jigsaw")}
                            </Typography>
                        </Box>
                    <PlayToolbarScrollRow
                        sx={{
                            mb: 0,
                            mt: 0,
                            minWidth: 0,
                            width: "auto",
                            flex: "0 1 auto",
                            ml: "auto",
                            ...panelSurfaceSx,
                            borderRadius: isPromoPortraitCapture ? 3 : 2,
                            px: isPromoPortraitCapture ? 1.5 : 0.75,
                            py: isPromoPortraitCapture ? 0.75 : 0.35,
                            ...(isCaptureLandscape || isPromoPortraitCapture
                                ? {
                                      ml: 0,
                                      width: "auto",
                                      ...(isPromoPortraitCapture
                                          ? { "& > div > div": { gap: 1.5 } }
                                          : null),
                                  }
                                : null),
                        }}
                    >
                            {canShareJigsaw ? (
                                <SocialShareDropdown
                                    compact
                                    puzzleTitle={title || t("nav.jigsaw", "Jigsaw")}
                                    puzzleId={jigsawShareId}
                                    puzzleType="JIGSAW"
                                    shareUrl={jigsawShareUrl}
                                />
                            ) : null}
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0,
                                    flexShrink: 0,
                                    "& .MuiTypography-root": {
                                        color: PLAY_TOOLBAR_FG,
                                        fontSize: isPromoPortraitCapture
                                            ? "2rem"
                                            : isCaptureLandscape
                                              ? "1.35rem"
                                              : "0.95rem",
                                        fontWeight: isPromoPortraitCapture ? 800 : 700,
                                    },
                                    ...(isCaptureLandscape || isPromoPortraitCapture
                                        ? {
                                              minWidth: isPromoPortraitCapture ? "6rem" : "4.5rem",
                                          }
                                        : null),
                                }}
                            >
                                <PlayElapsedTimer
                                    persistKey={
                                        jigsawId ? `jigsaw-play-elapsed:${jigsawId}` : null
                                    }
                                    running={!isSolvedScatter && !timerEffectivelyPaused}
                                    ariaLabel={t("puzzle.play-elapsed-time", "Elapsed time")}
                                />
                                {!isSolvedScatter ? (
                                    <Tooltip
                                        title={
                                            timerEffectivelyPaused
                                                ? t("puzzle.jigsaw.resume-timer", "Resume timer")
                                                : t("puzzle.jigsaw.pause-timer", "Pause timer")
                                        }
                                        arrow
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                if (visibilityPaused) {
                                                    continuePlaying();
                                                    setTimerPaused((prev) => {
                                                        if (
                                                            prev
                                                            && timerPauseStartedAtRef.current != null
                                                            && playStartMsRef.current != null
                                                        ) {
                                                            playStartMsRef.current +=
                                                                Date.now() - timerPauseStartedAtRef.current;
                                                            timerPauseStartedAtRef.current = null;
                                                        }
                                                        return false;
                                                    });
                                                    return;
                                                }
                                                handleToggleTimerPause();
                                            }}
                                            sx={{
                                                flexShrink: 0,
                                                ml: -0.75,
                                                ...toolbarIconSx,
                                            }}
                                            aria-label={
                                                timerEffectivelyPaused
                                                    ? t("puzzle.jigsaw.resume-timer", "Resume timer")
                                                    : t("puzzle.jigsaw.pause-timer", "Pause timer")
                                            }
                                            aria-pressed={timerEffectivelyPaused}
                                        >
                                            {timerEffectivelyPaused ? (
                                                <PlayArrowIcon fontSize="medium" />
                                            ) : (
                                                <PauseIcon fontSize="medium" />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                ) : null}
                            </Box>
                        <Tooltip title={t("puzzle.jigsaw.zoom-out", "Zoom out")} arrow>
                            <span>
                                <IconButton
                                    size="medium"
                                    data-play-toolbar-no-drag
                                    onClick={() => handleBoardZoomStep(-1)}
                                    disabled={!canZoomOut}
                                    sx={{ flexShrink: 0, ...toolbarIconSx }}
                                    aria-label={t("puzzle.jigsaw.zoom-out", "Zoom out")}
                                >
                                    <ZoomOutIcon fontSize="medium" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        {boardZoom !== 1 ? (
                            <Tooltip title={t("puzzle.jigsaw.zoom-reset", "Reset zoom")} arrow>
                                <Box
                                    component="button"
                                    type="button"
                                    data-play-toolbar-no-drag
                                    onClick={handleBoardZoomReset}
                                    aria-label={t("puzzle.jigsaw.zoom-reset", "Reset zoom")}
                                    sx={{
                                        flexShrink: 0,
                                        border: "none",
                                        background: "none",
                                        cursor: "pointer",
                                        px: 0.25,
                                        color: PLAY_TOOLBAR_FG,
                                        fontWeight: 700,
                                        fontSize: "0.85rem",
                                        lineHeight: 1.2,
                                        whiteSpace: "nowrap",
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {`${Math.round(boardZoom * 100)}%`}
                                </Box>
                            </Tooltip>
                        ) : null}
                        <Tooltip title={t("puzzle.jigsaw.zoom-in", "Zoom in")} arrow>
                            <span>
                                <IconButton
                                    size="medium"
                                    data-play-toolbar-no-drag
                                    onClick={() => handleBoardZoomStep(1)}
                                    disabled={!canZoomIn}
                                    sx={{ flexShrink: 0, ...toolbarIconSx }}
                                    aria-label={t("puzzle.jigsaw.zoom-in", "Zoom in")}
                                >
                                    <ZoomInIcon fontSize="medium" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip
                            title={t(
                                "puzzle.jigsaw.sweep-misplaced",
                                "Remove pieces that are not in the correct place",
                            )}
                            arrow
                        >
                            <span>
                                <IconButton
                                    size="medium"
                                    onClick={handleSweepMisplacedFromBoard}
                                    disabled={!hasMisplacedOnBoard}
                                    sx={{ flexShrink: 0, ...toolbarIconSx }}
                                    aria-label={t(
                                        "puzzle.jigsaw.sweep-misplaced",
                                        "Remove pieces that are not in the correct place",
                                    )}
                                >
                                    <CleaningServicesIcon fontSize="medium" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip
                            title={t(
                                "puzzle.jigsaw.placement-hint-tip",
                                "Place one piece on its home (5 coins)",
                            )}
                            arrow
                        >
                            <span>
                        <IconButton
                            size="medium"
                            data-play-toolbar-no-drag
                            disabled={isSolvedScatter || placementHintBusy}
                            onClick={() => {
                                void handlePlacementHintClick();
                            }}
                            sx={{ flexShrink: 0, ...toolbarIconSx }}
                            aria-label={t("puzzle.jigsaw.placement-hint", "Hint")}
                            aria-busy={placementHintBusy}
                        >
                            {placementHintBusy ? (
                                <CircularProgress
                                    size={
                                        isPromoPortraitCapture
                                            ? 28
                                            : isCaptureLandscape
                                              ? 22
                                              : 20
                                    }
                                    thickness={5}
                                    sx={{ color: PLAY_TOOLBAR_FG }}
                                />
                            ) : (
                                <LightbulbOutlinedIcon fontSize="medium" />
                            )}
                        </IconButton>
                            </span>
                        </Tooltip>
                        {imageUrl ? (
                            <Tooltip
                                title={
                                    referenceOnScreen
                                        ? t("puzzle.jigsaw.hide-image", "Hide image")
                                        : t("puzzle.jigsaw.show-image", "Show image")
                                }
                                arrow
                            >
                                <IconButton
                                    size="medium"
                                    data-play-toolbar-no-drag
                                    onClick={toggleReferenceOnScreen}
                                    aria-pressed={referenceOnScreen}
                                    sx={{ flexShrink: 0, ...toolbarIconSx }}
                                    aria-label={
                                        referenceOnScreen
                                            ? t("puzzle.jigsaw.hide-image", "Hide image")
                                            : t("puzzle.jigsaw.show-image", "Show image")
                                    }
                                >
                                    <ImageOutlinedIcon fontSize="medium" />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                        {colorBasketsAvailable && !isSolvedScatter && !colorBasketMode ? (
                            <Tooltip
                                title={t("puzzle.jigsaw.color-baskets", "Color baskets")}
                                arrow
                            >
                                <IconButton
                                    size="medium"
                                    data-play-toolbar-no-drag
                                    onClick={() => setColorBasketOverlayOpen(true)}
                                    sx={{ flexShrink: 0, ...toolbarIconSx }}
                                    aria-label={t("puzzle.jigsaw.color-baskets", "Color baskets")}
                                >
                                    <FaShoppingBasket
                                        size={
                                            isPromoPortraitCapture
                                                ? 36
                                                : isCaptureLandscape
                                                  ? 24
                                                  : 22
                                        }
                                        color="currentColor"
                                    />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                        <Tooltip title={t("nav.settings", "Settings")} arrow>
                            <IconButton
                                size="medium"
                                data-play-toolbar-no-drag
                                onClick={(e) => setSettingsAnchor(e.currentTarget)}
                                sx={{ flexShrink: 0, ...toolbarIconSx }}
                                aria-label={t("nav.settings", "Settings")}
                                aria-haspopup="true"
                                aria-expanded={Boolean(settingsAnchor)}
                            >
                                <SettingsIcon fontSize="medium" />
                            </IconButton>
                        </Tooltip>
                    </PlayToolbarScrollRow>
                        {placementHintMessage || placementHintCountdownSec != null ? (
                            <Box
                                component="span"
                                sx={{
                                    flexShrink: 0,
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    whiteSpace: "nowrap",
                                    fontVariantNumeric: "tabular-nums",
                                    minWidth: placementHintCountdownSec != null ? 280 : undefined,
                                    bgcolor:
                                        placementHintMessageSeverity === "success"
                                            ? "#0B3D2E"
                                            : "#5C3B00",
                                    color:
                                        placementHintMessageSeverity === "success"
                                            ? "#E7FFF4"
                                            : "#FFF4D6",
                                    border: "1px solid",
                                    borderColor:
                                        placementHintMessageSeverity === "success"
                                            ? "#2E7D57"
                                            : "#C98400",
                                    boxShadow: 1,
                                }}
                            >
                                {placementHintCountdownSec != null
                                    ? isAuthenticated
                                        ? t("puzzle.jigsaw.placement-hint-consumed-countdown", {
                                              seconds: placementHintCountdownSec,
                                          })
                                        : t("puzzle.jigsaw.placement-hint-guest-countdown", {
                                              seconds: placementHintCountdownSec,
                                          })
                                    : placementHintMessage}
                            </Box>
                        ) : null}
                    </Box>
                    ) : null}
                    {/* Always mounted so BGM keeps playing when settings closes */}
                    {audioPrefsReady ? (
                        <Box
                            ref={bgmButtonWrapRef}
                            aria-hidden
                            sx={{
                                position: "fixed",
                                left: -9999,
                                top: 0,
                                width: 1,
                                height: 1,
                                overflow: "hidden",
                                opacity: 0,
                                pointerEvents: "none",
                            }}
                        >
                            <AudioButton
                                key={`bgm-${bgmAutoPlay}`}
                                tracks={PUZZLE_BGM_TRACKS}
                                defaultPlaying={bgmAutoPlay}
                                cloudUserId={cloudUserId}
                                leadingDivider={false}
                                onPlayingChange={setBgmPlaying}
                            />
                        </Box>
                    ) : null}
                    <Popover
                        open={Boolean(settingsAnchor)}
                        anchorEl={settingsAnchor}
                        onClose={() => setSettingsAnchor(null)}
                        disableScrollLock
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        slotProps={{
                            paper: {
                                sx: {
                                    mt: 0.75,
                                    p: 1.5,
                                    minWidth: 200,
                                    borderRadius: 2,
                                    boxShadow: 4,
                                },
                            },
                        }}
                    >
                        {isAuthenticated ? (
                            <>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
                                >
                                    {t("puzzle.sliding.settings-wallet", "Balance")}
                                </Typography>
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr 1fr",
                                        gap: 0.75,
                                        mb: 1.25,
                                    }}
                                >
                                    {[
                                        {
                                            key: "play",
                                            label: t("puzzle.sliding.settings-play-credits", "Play"),
                                            value: walletPlayCredits,
                                            icon: (
                                                <TollIcon sx={{ fontSize: "1.1rem", color: "#2761F5" }} />
                                            ),
                                        },
                                        {
                                            key: "hints",
                                            label: t("puzzle.jigsaw.settings-hint-coins", "Coins"),
                                            value: walletHintCredits,
                                            icon: (
                                                <LightbulbOutlinedIcon
                                                    sx={{ fontSize: "1.1rem", color: "#3BA666" }}
                                                />
                                            ),
                                        },
                                        {
                                            key: "coins",
                                            label: t("puzzle.jigsaw.settings-puzzle-coins", "Puzzle coins"),
                                            value: walletPuzzleCoins,
                                            icon: (
                                                <MonetizationOnIcon
                                                    sx={{ fontSize: "1.1rem", color: "#d97706" }}
                                                />
                                            ),
                                        },
                                    ].map((item) => (
                                        <Box
                                            key={item.key}
                                            sx={{
                                                textAlign: "center",
                                                py: 0.75,
                                                px: 0.5,
                                                borderRadius: 1,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                bgcolor: "action.hover",
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: "block", fontWeight: 600, lineHeight: 1.2 }}
                                            >
                                                {item.label}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: 0.35,
                                                    mt: 0.25,
                                                }}
                                            >
                                                {item.icon}
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 800, lineHeight: 1.2 }}
                                                >
                                                    {walletBalancesLoading && item.value == null
                                                        ? "…"
                                                        : Number(item.value ?? 0).toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                                <Divider sx={{ my: 1.25 }} />
                            </>
                        ) : null}
                        {imageUrl ? (
                            <>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
                                >
                                    {t("puzzle.jigsaw.reference-image-menu")}
                                </Typography>
                                <MenuItem
                                    onClick={handleOpenFullImage}
                                    sx={{
                                        borderRadius: 1,
                                        mx: -0.5,
                                        minHeight: 32,
                                        py: 0.5,
                                        fontSize: "0.8125rem",
                                    }}
                                >
                                    {t("puzzle.jigsaw.open-full-image")}
                                </MenuItem>
                                <MenuItem
                                    onClick={handleToggleReferenceFromMenu}
                                    sx={{
                                        borderRadius: 1,
                                        mx: -0.5,
                                        minHeight: 32,
                                        py: 0.5,
                                        fontSize: "0.8125rem",
                                    }}
                                >
                                    {referenceOnScreen
                                        ? t("puzzle.jigsaw.hide-on-screen")
                                        : t("puzzle.jigsaw.show-on-screen")}
                                </MenuItem>
                                <Divider sx={{ my: 1.25 }} />
                            </>
                        ) : null}
                        <BoardMatColorSwitcher
                            variant="panel"
                            matId={boardMatId}
                            onMatIdChange={setBoardMatId}
                            ariaLabel={t("puzzle.jigsaw.board-mat-color", "Board mat color")}
                        />
                        <Divider sx={{ my: 1.25 }} />
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.75,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {t("puzzle.jigsaw.die-lines", "Die lines")}
                                </Typography>
                                <Tooltip
                                    title={
                                        showDieLines
                                            ? t("puzzle.jigsaw.hide-die-lines", "Hide die lines")
                                            : t("puzzle.jigsaw.show-die-lines", "Show die lines")
                                    }
                                    arrow
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => setShowDieLines((v) => !v)}
                                        sx={{
                                            color: showDieLines ? "#3BA666" : "text.disabled",
                                            ...toolbarIconSx,
                                        }}
                                        aria-label={
                                            showDieLines
                                                ? t("puzzle.jigsaw.hide-die-lines", "Hide die lines")
                                                : t("puzzle.jigsaw.show-die-lines", "Show die lines")
                                        }
                                        aria-pressed={showDieLines}
                                    >
                                        <ExtensionIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {t("puzzle.jigsaw.background-music", "Background music")}
                                </Typography>
                                <Tooltip
                                    title={
                                        bgmPlaying
                                            ? t("audio-button.mute")
                                            : t("audio-button.play")
                                    }
                                    arrow
                                >
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={!audioPrefsReady}
                                            onClick={() => {
                                                const btn =
                                                    bgmButtonWrapRef.current?.querySelector(
                                                        "button",
                                                    );
                                                btn?.click();
                                            }}
                                            sx={{
                                                color: "#3BA666",
                                                ...toolbarIconSx,
                                            }}
                                            aria-label={
                                                bgmPlaying
                                                    ? t("audio-button.mute")
                                                    : t("audio-button.play")
                                            }
                                        >
                                            {bgmPlaying ? (
                                                <MusicNoteIcon fontSize="small" />
                                            ) : (
                                                <MusicOffIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {t("puzzle.jigsaw.sound-effects", "Sound effects")}
                                </Typography>
                                <Tooltip
                                    title={
                                        soundEffectsEnabled
                                            ? t(
                                                  "puzzle.jigsaw.mute-sound-effects",
                                                  "Mute sound effects",
                                              )
                                            : t(
                                                  "puzzle.jigsaw.enable-sound-effects",
                                                  "Enable sound effects",
                                              )
                                    }
                                    arrow
                                >
                                    <IconButton
                                        size="small"
                                        onClick={handleToggleSoundEffects}
                                        sx={{
                                            color: "#3BA666",
                                            ...toolbarIconSx,
                                        }}
                                        aria-label={
                                            soundEffectsEnabled
                                                ? t(
                                                      "puzzle.jigsaw.mute-sound-effects",
                                                      "Mute sound effects",
                                                  )
                                                : t(
                                                      "puzzle.jigsaw.enable-sound-effects",
                                                      "Enable sound effects",
                                                  )
                                        }
                                        aria-pressed={!soundEffectsEnabled}
                                    >
                                        {soundEffectsEnabled ? (
                                            <VolumeUpIcon fontSize="small" />
                                        ) : (
                                            <VolumeOffIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        {isAdminRole && !colorBasketMode ? (
                            <>
                                <Divider sx={{ my: 1.25 }} />
                                <Typography
                                    variant="caption"
                                    sx={{ display: "block", mb: 0.75, fontWeight: 700, color: "#1e3a8a" }}
                                >
                                    {t("puzzle.jigsaw.developer-section", "Developer")}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    disabled={total < 2 || isSolvedScatter}
                                    onClick={handleAdminPlaceAllButOne}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        justifyContent: "flex-start",
                                        borderColor: "#1e3a8a",
                                        color: "#1e3a8a",
                                        "&:hover": {
                                            borderColor: "#1e3a8a",
                                            bgcolor: "rgba(30, 58, 138, 0.08)",
                                        },
                                    }}
                                >
                                    {t("puzzle.jigsaw.place-all-but-one", "Place all −1")}
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    disabled={placedCountScatter === 0}
                                    onClick={handleResetScatter}
                                    sx={{
                                        mt: 0.75,
                                        textTransform: "none",
                                        fontWeight: 600,
                                        justifyContent: "flex-start",
                                        borderColor: "#1e3a8a",
                                        color: "#1e3a8a",
                                        "&:hover": {
                                            borderColor: "#1e3a8a",
                                            bgcolor: "rgba(30, 58, 138, 0.08)",
                                        },
                                    }}
                                >
                                    {t("puzzle.jigsaw.reset-board", "Reset")}
                                </Button>
                            </>
                        ) : null}
                    </Popover>

                    {paths ? (
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: isCaptureLandscape ? "stretch" : "stretch",
                                overflowX: "hidden",
                                overflowY: isCaptureLandscape ? "hidden" : "visible",
                                width: "100%",
                                pt: 0,
                            }}
                        >
                            {renderPlayGridContent()}
                        </Box>
                    ) : null}

                    {dragging && paths && (() => {
                        const cellPxLocal = BOARD_SIZE / grid;
                        const { left, top, scale } = getDragGhostBasePosition(dragging, cellPxLocal);
                        const groupMembers = dragging.groupDragMembers;
                        const isGroupGhost = Array.isArray(groupMembers) && groupMembers.length > 1;

                        return (
                            <div
                                ref={dragGhostRef}
                                style={{
                                    position: "fixed",
                                    left,
                                    top,
                                    pointerEvents: "none",
                                    zIndex: 9999,
                                    transform: `scale(${scale})`,
                                    transformOrigin: "top left",
                                    willChange: "transform",
                                }}
                            >
                                {isGroupGhost ? (
                                    groupMembers.map((member) => (
                                        <div
                                            key={member.pieceIndex}
                                            style={{
                                                position: "absolute",
                                                left: member.dx,
                                                top: member.dy,
                                            }}
                                        >
                                            <PieceVisual
                                                pieceIndex={member.pieceIndex}
                                                imageUrl={imageUrl}
                                                grid={grid}
                                                paths={paths}
                                                showGridCoords={false}
                                                bevel3d={false}
                                                anchorToCell
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <PieceVisual
                                        pieceIndex={dragging.pieceIndex}
                                        imageUrl={imageUrl}
                                        grid={grid}
                                        paths={paths}
                                        showGridCoords={false}
                                        bevel3d={false}
                                        anchorToCell={!(dragging.fromScattered && dragging.listTray)}
                                    />
                                )}
                            </div>
                        );
                    })()}

                    <Dialog
                        open={imageDialogOpen}
                        onClose={() => setImageDialogOpen(false)}
                        maxWidth="lg"
                        fullWidth
                    >
                        <DialogContent sx={{ p: 1.5, position: "relative" }}>
                            <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <IconButton
                                    onClick={() => setImageDialogOpen(false)}
                                    sx={{ bgcolor: "background.paper", boxShadow: 1, "&:hover": { bgcolor: "background.paper" } }}
                                    aria-label="Close"
                                >
                                    <CloseIcon />
                                </IconButton>
                                <IconButton
                                    onClick={() => setShowDieLines((v) => !v)}
                                    sx={{ bgcolor: showDieLines ? "#3BA666" : "background.paper", color: showDieLines ? "#fff" : "text.primary", boxShadow: 1, "&:hover": { bgcolor: showDieLines ? "#2e7d32" : "action.hover" } }}
                                    aria-label="Toggle die lines"
                                >
                                    <ExtensionIcon />
                                </IconButton>
                            </Box>
                            {imageUrl && (
                                <Box sx={{ position: "relative", width: "100%", display: "flex", justifyContent: "center" }}>
                                    <Box
                                        component="img"
                                        src={imageUrl}
                                        alt="Original puzzle image"
                                        sx={{ width: "100%", maxHeight: "85vh", objectFit: "contain", display: "block" }}
                                    />
                                    {showDieLines && paths && (
                                        <JigsawDieLineOverlay
                                            pieces={paths}
                                            bevel3d
                                            opacity={0.9}
                                            strokeWidth={1}
                                            zIndex={1}
                                        />
                                    )}
                                </Box>
                            )}
                        </DialogContent>
                    </Dialog>

                    <SuccessCard
                        open={showSuccess}
                        onClose={() => setShowSuccess(false)}
                        stats={successStats}
                        speedRank={successSpeedRank}
                        showSpeedInvite
                        guest={!isAuthenticated}
                        celebrationSoundEnabled={soundEffectsEnabled}
                        showRating={isAuthenticated}
                        ratingValue={successRating}
                        ratingBusy={successRatingBusy}
                        onRate={async (stars) => {
                            setSuccessRating(stars);
                            setSuccessRatingBusy(true);
                            try {
                                await FeedbackService.submitFeedback({
                                    cloudUserId,
                                    stars,
                                    details: {
                                        puzzleType: "JIGSAW",
                                        jigsawId,
                                        storyId,
                                    },
                                });
                            } catch (e) {
                                Logger.error("Jigsaw rating failed:", e);
                            } finally {
                                setSuccessRatingBusy(false);
                            }
                        }}
                        onNext={() => {
                            setShowSuccess(false);
                            if (!isAuthenticated) {
                                void AuthService.signup().catch((e) =>
                                    Logger.error("Signup redirect:", e)
                                );
                            } else {
                                navigate("/puzzles/jigsaw");
                            }
                        }}
                    />

                    {/* Welcome-back popup disabled — timer still pauses while tab is hidden
                    <GamePausedOverlay
                        open={pauseOverlayOpen}
                        onContinue={() => {
                            continuePlaying();
                            setTimerPaused((prev) => {
                                if (
                                    prev
                                    && timerPauseStartedAtRef.current != null
                                    && playStartMsRef.current != null
                                ) {
                                    playStartMsRef.current +=
                                        Date.now() - timerPauseStartedAtRef.current;
                                    timerPauseStartedAtRef.current = null;
                                }
                                return false;
                            });
                        }}
                    />
                    */}

                    <Dialog
                        open={basketInsufficientCoinsOpen}
                        onClose={() => setBasketInsufficientCoinsOpen(false)}
                        maxWidth="xs"
                        fullWidth
                        aria-labelledby="jigsaw-basket-insufficient-coins-title"
                    >
                        <DialogTitle id="jigsaw-basket-insufficient-coins-title">
                            {t("puzzle.jigsaw.basket-unlock-insufficient-title", "Not enough coins")}
                        </DialogTitle>
                        <DialogContent>
                            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                                {t("puzzle.jigsaw.basket-unlock-insufficient", {
                                    count: basketUnlockCoinCost,
                                    defaultValue:
                                        "Not enough coins. Each basket costs {{count}} coins.",
                                })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                {t(
                                    "puzzle.jigsaw.basket-unlock-insufficient-hint",
                                    "Get more coins with a subscription, then come back to unlock this basket.",
                                )}
                            </Typography>
                        </DialogContent>
                        <DialogActions sx={{ px: 2, pb: 2 }}>
                            <Button
                                onClick={() => {
                                    setBasketInsufficientCoinsOpen(false);
                                    handleBackFromBoard();
                                }}
                            >
                                {t("btn.cancel", "Cancel")}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setBasketInsufficientCoinsOpen(false);
                                    navigate("/subscription/plans");
                                }}
                                sx={{ bgcolor: "#3BA666", "&:hover": { bgcolor: "#2e8a52" } }}
                            >
                                {t("puzzle.jigsaw.basket-unlock-get-coins", "Get coins")}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <JigsawColorBasketOverlay
                        open={colorBasketOverlayOpen}
                        onClose={handleColorBasketOverlayClose}
                        storyId={storyId}
                        jigsawId={selectedTemplate?.id}
                        grid={puzzleGrid}
                        imageUrl={imageUrl}
                        puzzleTitle={title}
                        isAdminRole={isAdminRole}
                    />
                    <JigsawProgressGift gift={progressGift} onClose={showNextProgressGift} />
                </>
            )}
            </Box>
        </Box>
        </>
    );
}
